import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import WeekNavigator from '../report/WeekNavigator';
import SessionDetailPanel, { ScheduleSession } from './SessionDetailPanel';
import BulkEditPanel from './BulkEditPanel';
import ScopeChoiceModal, { EditKind, EditScope } from './ScopeChoiceModal';
import ConfirmationModal from '../ConfirmationModal';
import { GridLockToggle, UnlockPromptModal, useLockedClickGuard } from './GridLock';
import CancelDateModal from './CancelDateModal';
import SessionDefaultsModal from './SessionDefaultsModal';
import BulkPeriodModal from './BulkPeriodModal';
import { occursOnDate, DAY_OFFSETS } from './scheduleOccurrences';
import { timeToMins, minsToTime, buildSlots } from './scheduleTime';
import * as rules from './scheduleRules';
import { ClashContext, LocalException, SessionConflict } from './scheduleRules';
import { ChildProfile } from '../../models/childProfile';
import { StaffProfile } from '../../models/staffProfile';
import { UserDataService } from '../../services/userDataService';
import { ScheduleDataService, HydratedScheduleException } from '../../services/scheduleDataService';
import { AttendanceDataService, AttendanceRecord } from '../../services/attendanceDataService';
import { LayoutOutletContext } from '../Layout';
import { HelpOffer } from '../../help/HelpOffer';
import { useAppState } from '../../context/AppStateContext';
import { toDateString, getMonday, formatDisplayDate, addDays } from '../../services/dateUtils';
import './schedule.scss';

interface WeeklyScheduleProps {
  childId: string | null;
  childProfiles: ChildProfile[];
  weekStart: string | null;
  onWeekChange: (monday: string, friday: string) => void;
  /** When set, only sessions assigned to this teacher (user_id) are rendered. */
  teacherFilterId?: number | null;
}

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const WEEKEND = ['Saturday', 'Sunday'];
const SESSION_OPTIONS = [15, 30, 45, 60];

const DEFAULT_START = '09:00';
const DEFAULT_END = '15:30';
const SESSION_HEIGHT = 30;
const MIN_LANES = 2;
const DETAIL_PANEL_WIDTH = 320;

// Map API exception rows onto the local mirror. Rows whose parent rule isn't
// in the map are dropped — that happens for daily rules, which the weekly grid
// filters out at hydration. Shared by the mount hydration and the post-bulk
// refresh so both build the mirror identically.
function mapExceptionsToLocal(
  rows: HydratedScheduleException[],
  localIdByServer: Map<number, string>,
): LocalException[] {
  const out: LocalException[] = [];
  for (const ex of rows) {
    const localId = localIdByServer.get(ex.scheduleItemServerId);
    if (!localId) continue;
    if (ex.status === 'cancelled') {
      out.push({
        sessionId: localId,
        date: ex.date,
        status: 'cancelled',
        serverId: ex.serverId,
        reason: ex.reason ?? null,
      });
    } else if (ex.status === 'modified') {
      out.push({
        sessionId: localId,
        date: ex.date,
        status: 'modified',
        serverId: ex.serverId,
        overrideStartTime: ex.overrideStartTime,
        overrideEndTime: ex.overrideEndTime,
        overrideTeacherIds: ex.teacherIds,
        overrideLocationIds: ex.locationIds,
      });
    }
  }
  return out;
}

// ── Drag types ──────────────────────────────────────────────────

// 'move-pending' is a press on a bar that hasn't left its slot yet — still
// just a click (select) until the pointer reaches another cell.
type DragType = 'none' | 'create' | 'resize-left' | 'resize-right' | 'move-pending' | 'move';

// Where a bar being dragged would land: day plus the moved times (duration
// kept). Rendered in place of the session until the drop.
// Fields that change when a session runs — an edit to any of them can
// disturb attendance recorded against it.
const RETIME_FIELDS: Array<keyof ScheduleSession> =
  ['startTime', 'endTime', 'startDate', 'endDate', 'dayOfWeek', 'frequency'];

interface MovePreview {
  sessionId: string;
  day: string;
  startTime: string;
  endTime: string;
}

interface DraftBounds {
  day: string;
  lane: number;
  startIdx: number;
  endIdx: number;
}

interface ResizePreview {
  sessionId: string;
  startTime: string;
  endTime: string;
}

// ── Component ───────────────────────────────────────────────────

const WeeklySchedule: React.FC<WeeklyScheduleProps> = ({
  childId,
  childProfiles,
  weekStart,
  onWeekChange,
  teacherFilterId = null,
}) => {
  // Hide the left-nav sidebar while the session detail panel is open (same pattern as the post editor)
  const { setSidebarOpen } = useOutletContext<LayoutOutletContext>();

  // Staff pool for teacher selection — only users holding the Editor role are
  // assignable teachers. The backend filters via editorsOnly so role data
  // doesn't need to be exposed on the general staff endpoint.
  const [editorStaff, setEditorStaff] = useState<StaffProfile[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await UserDataService.fetchStaffProfiles({ editorsOnly: true });
        if (cancelled) return;
        const sorted = [...data].sort((a, b) => a.name.localeCompare(b.name));
        setEditorStaff(sorted);
      } catch (err) {
        console.error('Failed to fetch staff for schedule', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Config state — defaults are seeded from per-org config (loaded into
  // AppStateContext at app startup) the first time it becomes available.
  // The page-local controls below still let the user tweak per-session;
  // we deliberately don't re-sync after the initial seed so an admin
  // changing org defaults mid-session won't yank the form out from
  // under the user.
  const { orgConfig } = useAppState();
  const [includeWeekends, setIncludeWeekends] = useState(false);
  const [sessionMinutes, setSessionMinutes] = useState(30);
  const [dayStartTime, setDayStartTime] = useState(DEFAULT_START);
  const [dayEndTime, setDayEndTime] = useState(DEFAULT_END);
  const orgConfigSeededRef = useRef(false);
  useEffect(() => {
    if (!orgConfig || orgConfigSeededRef.current) return;
    setIncludeWeekends(orgConfig.includeWeekends);
    setSessionMinutes(orgConfig.defaultSessionIntervalMinutes);
    setDayStartTime(orgConfig.workingDayStart);
    setDayEndTime(orgConfig.workingDayEnd);
    orgConfigSeededRef.current = true;
  }, [orgConfig]);

  // Session state
  const [sessions, setSessions] = useState<ScheduleSession[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  // Additional sessions selected via Ctrl/Cmd-click. Together with
  // `selectedSessionId` (the "primary" — driver for the side panel single
  // mode) these form the full selection. When `extraSelectedIds.size > 0`
  // the side panel switches to bulk-edit mode.
  const [extraSelectedIds, setExtraSelectedIds] = useState<Set<string>>(new Set());
  const isSessionSelected = (id: string | null | undefined): boolean =>
    id != null && (id === selectedSessionId || extraSelectedIds.has(id));
  const selectedCount =
    (selectedSessionId ? 1 : 0) + extraSelectedIds.size;
  const isMultiSelect = selectedCount > 1;
  const allSelectedIds: string[] = (() => {
    const out: string[] = [];
    if (selectedSessionId) out.push(selectedSessionId);
    extraSelectedIds.forEach(id => { if (id !== selectedSessionId) out.push(id); });
    return out;
  })();
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelDefaults, setPanelDefaults] = useState<{
    childId: number | null;
    teacherIds: number[];
    locationIds: number[];
  }>({ childId: childId ? Number(childId) : null, teacherIds: [], locationIds: [] });

  // Defaults modal (opened from the "Next new session" pill). Staff chosen
  // there are "pinned": the automatic snapshot that follows every session
  // save keeps deriving child/locations from the saved session but leaves
  // pinned teachers alone. The ref mirrors the state so the promise
  // callbacks that snapshot defaults never read a stale pin flag.
  const [defaultsModalOpen, setDefaultsModalOpen] = useState(false);
  // Length of a session created by a single click (no drag). Editable in the
  // defaults modal; drag-creates ignore it and size by grid slots as dragged.
  const [defaultSessionLengthMinutes, setDefaultSessionLengthMinutes] = useState(60);
  const [teachersPinned, setTeachersPinned] = useState(false);
  const teachersPinnedRef = useRef(false);
  const setTeachersPinnedAll = (v: boolean) => {
    teachersPinnedRef.current = v;
    setTeachersPinned(v);
  };

  // Snapshot new-session defaults from a just-saved session. All automatic
  // defaults updates funnel through here so the pin rule lives in one place.
  const snapshotDefaultsFromSave = (next: {
    childId: number | null;
    teacherIds: number[];
    locationIds: number[];
  }) => {
    setPanelDefaults(prev => ({
      childId: next.childId,
      teacherIds: teachersPinnedRef.current ? prev.teacherIds : [...next.teacherIds],
      locationIds: [...next.locationIds],
    }));
  };

  // UI state
  const [hoveredCell, setHoveredCell] = useState<string | null>(null);
  const [draftBounds, setDraftBounds] = useState<DraftBounds | null>(null);
  const [resizePreview, setResizePreview] = useState<ResizePreview | null>(null);
  const [movePreview, setMovePreview] = useState<MovePreview | null>(null);
  // The same, synchronously: what the drop reads (see handleMouseUp).
  const movePreviewRef = useRef<MovePreview | null>(null);

  // Edit-scope flow: when an edit is attempted on a *settled* persisted session
  // (one with serverId, no longer "fresh"), defer the write until the user picks
  // a scope. `snapshot` captures the session's pre-edit state so Cancel can revert.
  interface PendingEdit {
    sessionId: string;
    kind: EditKind;
    snapshot: ScheduleSession;
    // Local update already applied to state; if the user cancels we restore from snapshot.
    appliedUpdate: Partial<ScheduleSession> | 'delete';
    editDate: string | null;
    // Saved as 'persist' without asking — the edit changes something a
    // single-date exception can't hold. The modal only appears if that save
    // is refused, to say why, and then offers no "Just this date".
    persistOnly?: boolean;
  }
  const [pendingEdit, setPendingEdit] = useState<PendingEdit | null>(null);

  // Sessions that are still in their initial-creation window. While in this set,
  // edits live-update + auto-PATCH without going through the scope modal. A
  // session is removed from the set when the panel closes (or selection changes).
  const [freshSessionIds, setFreshSessionIds] = useState<Set<string>>(new Set());

  // Pending draft edits for a *settled* session. Edits accumulate here until the
  // user clicks Save (which opens the scope modal) or discards them.
  const [draftEdits, setDraftEdits] = useState<Partial<ScheduleSession> | null>(null);
  const [draftSnapshot, setDraftSnapshot] = useState<ScheduleSession | null>(null);

  const [localExceptions, setLocalExceptions] = useState<LocalException[]>([]);

  // App-standard confirm dialog (matches the post-delete / unsaved-changes
  // pattern used elsewhere). Promisified so call sites can `await`.
  interface ConfirmConfig {
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    isDeleting?: boolean;
    hideCancel?: boolean;
    help?: HelpOffer | null;
  }
  const [confirmConfig, setConfirmConfig] = useState<ConfirmConfig | null>(null);
  const confirmResolveRef = useRef<((ok: boolean) => void) | null>(null);
  const confirmAction = (config: ConfirmConfig): Promise<boolean> => {
    setConfirmConfig(config);
    return new Promise<boolean>(resolve => {
      confirmResolveRef.current = resolve;
    });
  };

  // Exception writes are optimistic: the bar moves before the server agrees,
  // and a rejection silently rolls it back. Without this the user just sees
  // their change snap away with no explanation. Surfaces the API's own message
  // where there is one (409 "An exception already exists for this date.").
  const reportExceptionWriteFailure = (attempted: string, err: unknown) => {
    const apiMessage = (err as { response?: { data?: { error?: string } } })
      ?.response?.data?.error;
    confirmAction({
      title: "Couldn't save that change",
      message: apiMessage
        ? `We couldn't ${attempted}: ${apiMessage} Your schedule is unchanged.`
        : `We couldn't ${attempted}. Your schedule is unchanged — please check your connection and try again.`,
      confirmText: 'OK',
      hideCancel: true,
    });
  };

  // ── Grid lock ──────────────────────────────────────────────
  // Locked by default. When locked, clicks on empty grid cells trigger the
  // unlock-prompt modal instead of starting a create-drag, and resize handles
  // on selected sessions are hidden.
  const [gridLocked, setGridLocked] = useState<boolean>(true);
  const [unlockPromptOpen, setUnlockPromptOpen] = useState<boolean>(false);
  const { flashKey: lockFlashKey, guard: lockedClickGuard } =
    useLockedClickGuard(() => setUnlockPromptOpen(true));

  // ── Cancel-for-date reason modal ───────────────────────────
  // Opened when the user clicks "Cancel for this date" on the side panel;
  // captures an optional reason (e.g. Bank holiday) before submitting the
  // schedule_exception POST.
  const [cancelDateModalOpen, setCancelDateModalOpen] = useState<boolean>(false);
  // Attendance-block message surfaced inside the cancel-reason modal when
  // submission is gated. Reset whenever the modal opens or the user
  // dismisses it; the parent confirms it cleared on each submit attempt.
  const [cancelDateError, setCancelDateError] = useState<string | null>(null);
  // Equivalent inline-error state for the scope-choice modal (one-off vs
  // persist edits/deletes).
  const [scopeError, setScopeError] = useState<string | null>(null);

  // ── Bulk period update ─────────────────────────────────────
  // Centred overlay for cancelling / restoring across a date range, which may
  // run well past the week on screen. Distinct from the right-hand
  // BulkEditPanel, which edits the sessions you've clicked in this week.
  const [bulkPeriodOpen, setBulkPeriodOpen] = useState(false);

  // Drag refs (no re-render needed)
  const dragTypeRef = useRef<DragType>('none');
  const dragDayRef = useRef('');
  const dragLaneRef = useRef(0);
  const dragStartIdxRef = useRef(0);
  const dragResizeSessionRef = useRef('');
  // Move drag: the session, and how far into it (minutes) the bar was
  // grabbed, so it stays under the pointer rather than jumping to its start.
  const dragMoveSessionRef = useRef('');
  const dragMoveGrabOffsetRef = useRef(0);
  const nextSessionId = useRef(1);
  // Latest attendance-checked retime of a fresh session (handleSessionUpdate).
  // An older check that resolves after a newer edit must not save.
  const freshRetimeSeqRef = useRef(0);

  // In-flight create promises keyed by local session id. Used to coalesce
  // edits made before the initial createScheduleItem returns: rather than
  // firing a duplicate create, we chain a PATCH onto the existing promise
  // that runs once serverId is known. Without this the user could lose
  // edits made in the few hundred milliseconds between drag-end and the
  // create response (or end up with two server-side rules and an orphan).
  const createPromisesRef = useRef<Map<string, Promise<{ id: number }>>>(new Map());

  // Authoritative serverId map keyed by local id. Updated *immediately* inside
  // the create-success callback so subsequent handleSessionUpdate calls see
  // the new id even before React has re-rendered with the updated sessions[]
  // state. Without this, the panel's teacher-toggle that fires right after
  // create resolves can read a stale session.serverId === undefined and
  // route into the first-time-create branch instead of live-PATCH — losing
  // the teacher write.
  const sessionServerIdsRef = useRef<Map<string, number>>(new Map());

  // ── "Now" indicator ────────────────────────────────────────
  // Ticks every 60 seconds — drives both the today-row highlight and the
  // vertical now-line within today's lane track.
  const [nowDate, setNowDate] = useState<Date>(new Date());
  useEffect(() => {
    const id = setInterval(() => setNowDate(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);
  const todayDateStr = toDateString(nowDate);

  // Derived
  const days = includeWeekends ? [...WEEKDAYS, ...WEEKEND] : WEEKDAYS;
  const slots = buildSlots(sessionMinutes, dayStartTime, dayEndTime);

  // Percent across the lanes track for the current moment, or null when
  // outside the configured working day.
  const nowPercentInDay: number | null = (() => {
    const [sh, sm] = dayStartTime.split(':').map(Number);
    const [eh, em] = dayEndTime.split(':').map(Number);
    const startMins = sh * 60 + sm;
    const endMins = eh * 60 + em;
    const total = endMins - startMins;
    if (total <= 0) return null;
    const nowMins = nowDate.getHours() * 60 + nowDate.getMinutes() + nowDate.getSeconds() / 60;
    if (nowMins < startMins || nowMins > endMins) return null;
    return ((nowMins - startMins) / total) * 100;
  })();
  const selectedChildIdNum = childId ? Number(childId) : null;

  // Mirror the top-level child filter into the new-session default childId,
  // but only when the filter is set to a specific child. When the filter is
  // "All" we keep whatever child was last used (so the user doesn't lose context).
  useEffect(() => {
    if (selectedChildIdNum !== null) {
      setPanelDefaults(prev => ({ ...prev, childId: selectedChildIdNum }));
    }
  }, [selectedChildIdNum]);


  // Defensive: when no session is selected, drop any orphan draft. Orphan drafts
  // were causing a spurious "discard unsaved changes?" prompt when the user
  // clicked any session bar after deselecting via empty-space click.
  useEffect(() => {
    if (selectedSessionId === null) {
      setDraftEdits(null);
      setDraftSnapshot(null);
    }
  }, [selectedSessionId]);

  // Hydrate from the API on mount — load all rules + cancelled exceptions and
  // populate sessions[] / localExceptions. Runs once. Daily rules (which the
  // weekly grid can't render as a single bar) are filtered server-side mapping.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [items, exceptions] = await Promise.all([
          ScheduleDataService.listScheduleItems(),
          ScheduleDataService.listScheduleExceptions(),
        ]);
        if (cancelled) return;

        // Convert hydrated rules to ScheduleSession[]. Lanes start at 0;
        // displayLanes computes overlap-safe positions at render time.
        const localIdByServer = new Map<number, string>();
        const newSessions: ScheduleSession[] = items.map(it => {
          const localId = `session-${nextSessionId.current++}`;
          localIdByServer.set(it.serverId, localId);
          // Mirror the serverId into the ref so any handleSessionUpdate
          // that fires before re-render can still find it.
          sessionServerIdsRef.current.set(localId, it.serverId);
          return {
            id: localId,
            lane: 0,
            dayOfWeek: it.dayOfWeek,
            startTime: it.startTime,
            endTime: it.endTime,
            childId: it.childId,
            teacherIds: [...it.teacherIds],
            locationIds: [...it.locationIds],
            frequency: it.frequency,
            startDate: it.startDate,
            endDate: it.endDate,
            serverId: it.serverId,
          };
        });

        setSessions(newSessions);
        setLocalExceptions(mapExceptionsToLocal(exceptions, localIdByServer));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to hydrate schedule', err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mirror of sessions[] for callbacks that need the current list without
  // being re-created on every change (the bulk-period refresh, below).
  const sessionsRef = useRef<ScheduleSession[]>([]);
  useEffect(() => { sessionsRef.current = sessions; }, [sessions]);

  // Re-read every exception from the API and rebuild the local mirror. The
  // bulk-period modal can write hundreds of rows across weeks the grid isn't
  // showing, so patching state by hand would be both fiddly and easy to get
  // subtly wrong after a partial result — one GET is authoritative.
  const refreshExceptions = async (): Promise<void> => {
    try {
      const rows = await ScheduleDataService.listScheduleExceptions();
      const localIdByServer = new Map<number, string>();
      for (const s of sessionsRef.current) {
        if (s.serverId !== undefined) localIdByServer.set(s.serverId, s.id);
      }
      setLocalExceptions(mapExceptionsToLocal(rows, localIdByServer));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to refresh schedule exceptions', err);
    }
  };

  // The viewed week's calendar date for a given weekday name.
  const getDateForWeekday = (dayName: string): string => {
    const base = weekStart ? new Date(weekStart + 'T00:00') : getMonday(new Date());
    base.setDate(base.getDate() + (DAY_OFFSETS[dayName] ?? 0));
    return toDateString(base);
  };

  // A start date is always the first session (rules.alignToStartDate).
  const alignToStartDate = (current: ScheduleSession, requested: Partial<ScheduleSession>) =>
    rules.alignToStartDate(current, requested, getDateForWeekday(current.dayOfWeek));


  // ── Attendance-block guard ───────────────────────────────────────
  // A scheduled session may not be modified, cancelled, or deleted if any
  // attendance record exists for it on or after the operation's effective
  // date. The user must clear those attendance records on the Attendance
  // page first. This helper is the single source of truth for the check
  // — every gated handler queries it before mutating.
  const FAR_FUTURE_DATE = '2099-12-31';
  const hasBlockingAttendance = async (
    scheduleItemServerId: number,
    childId: number,
    fromDate: string,
    toDate?: string,
    // Narrows a successful lookup to the records the change actually
    // disturbs. Never applied to the fail-closed placeholder below.
    affects?: (record: AttendanceRecord) => boolean,
  ): Promise<AttendanceRecord[]> => {
    try {
      const records = await AttendanceDataService.listAttendance(
        fromDate,
        toDate ?? FAR_FUTURE_DATE,
        { scheduleItemId: scheduleItemServerId, childId },
      );
      return affects ? records.filter(affects) : records;
    } catch (err) {
      // Fail closed: if we cannot verify, treat as blocking so we never
      // overwrite real data on the strength of a network error.
      // eslint-disable-next-line no-console
      console.error('Attendance guard query failed', err);
      return [{
        scheduleItemServerId, childId,
        attendanceDate: fromDate,
        actualArrival: null, actualDeparture: null,
        arrivalComment: null, departureComment: null,
      }];
    }
  };

  // Render the conflicting dates compactly for the inline block message.
  const formatBlockingDates = (records: AttendanceRecord[]): string => {
    const dates = Array.from(new Set(records.map(r => r.attendanceDate))).sort();
    if (dates.length === 0) return '';
    if (dates.length <= 3) return dates.map(formatDisplayDate).join(', ');
    return `${dates.slice(0, 3).map(formatDisplayDate).join(', ')} (and ${dates.length - 3} more)`;
  };

  const buildBlockMessage = (
    operation: 'modify' | 'cancel' | 'delete',
    records: AttendanceRecord[],
  ): string =>
    `Can't ${operation} this session — attendance has already been recorded on ${formatBlockingDates(records)}. ` +
    `Delete the attendance record${records.length === 1 ? '' : 's'} on the Attendance page first.`;

  // Attendance an in-place change to a rule would disturb. The records stay on
  // the same rule, so only those that stop being a session of it count (a
  // start date moved earlier on the same weekday disturbs nothing) — unless
  // the times change, which alters every past session they record.
  const attendanceDisturbedInPlace = (
    serverId: number,
    childId: number,
    before: ScheduleSession,
    after: ScheduleSession,
  ): Promise<AttendanceRecord[]> =>
    checkAttendanceWindow(serverId, childId, rules.inPlaceAttendanceWindow(before, after));

  // Fetch the records a rules.AttendanceWindow names.
  const checkAttendanceWindow = (
    serverId: number,
    childId: number,
    w: rules.AttendanceWindow,
  ): Promise<AttendanceRecord[]> =>
    hasBlockingAttendance(serverId, childId, w.from, w.to, w.affects && (r => w.affects!(r.attendanceDate)));

  // The standard refusal for an edit that attendance blocks, where there's no
  // modal already open to show it in.
  const showAttendanceBlock = (title: string, records: AttendanceRecord[]) =>
    confirmAction({
      title,
      message: buildBlockMessage('modify', records),
      confirmText: 'OK',
      hideCancel: true,
      // Blocked with no way forward on this screen — the guide is the way out.
      help: { guideKey: 'attendance.remove-record', label: 'How do I remove an attendance record?' },
    });

  // Project a recurring rule onto the currently-viewed week. Returns true if
  // the session occurs on its dayOfWeek in this week, given its date range
  // and frequency. Deliberately the same occursOnDate the clash checks use,
  // so a session can never clash on a date where it isn't drawn.
  //
  // Cancelled exceptions still render in the grid (greyed out) so the user
  // can restore them. The visibility check therefore does *not* filter on
  // cancellation — that's handled visually via isSessionCancelledThisWeek.
  const isSessionActiveThisWeek = (s: ScheduleSession): boolean =>
    occursOnDate(s, new Date(getDateForWeekday(s.dayOfWeek) + 'T00:00'));

  // Whitelist-aware set of child IDs the current user is allowed to see.
  // Home.fetchChildProfiles has already filtered `childProfiles` to the
  // user's whitelist (when one applies), so deriving from it gives us the
  // pre-filter we want for *all* schedule data. Admins / unrestricted users
  // get the full org list, so this is a no-op for them.
  const allowedChildIds = useMemo(
    () => new Set(childProfiles.map(c => c.id)),
    [childProfiles],
  );

  const isSessionVisible = (s: ScheduleSession): boolean => {
    // Whitelist guard applies only to sessions that actually have a child.
    // A null childId is a freshly-drawn session not yet assigned a child —
    // it isn't "another child's data", so the whitelist mustn't hide it, or
    // it vanishes on mouse-up and renders invisibly (getSessionAt filters it).
    if (s.childId != null && !allowedChildIds.has(s.childId)) return false;
    if (selectedChildIdNum !== null && s.childId !== selectedChildIdNum) return false;
    if (teacherFilterId !== null && !s.teacherIds.includes(teacherFilterId)) return false;
    // A bar mid-drag is drawn wherever it's being dragged to, even onto a
    // date its rule doesn't (yet) cover — e.g. earlier than its start date.
    if (movePreview && s.id === movePreview.sessionId) return true;
    return isSessionActiveThisWeek(s);
  };

  // What the grid draws: the sessions, with a bar mid-drag moved to its
  // preview day. Everything that lays out bars reads this, not `sessions`.
  const viewSessions = movePreview
    ? sessions.map(s => (s.id === movePreview.sessionId ? { ...s, dayOfWeek: movePreview.day } : s))
    : sessions;

  // True iff the session has a cancellation exception for the date its
  // dayOfWeek lands on in the currently-viewed week.
  const isSessionCancelledThisWeek = (s: ScheduleSession): boolean => {
    const dateStr = getDateForWeekday(s.dayOfWeek);
    return localExceptions.some(
      ex => ex.sessionId === s.id && ex.date === dateStr && ex.status === 'cancelled',
    );
  };

  const childName = childId
    ? (() => { const c = childProfiles.find(p => String(p.id) === childId); return c ? `${c.first_name} ${c.last_name}` : ''; })()
    : '';

  // ── Session helpers ─────────────────────────────────────────

  const getEffectiveTimes = (s: ScheduleSession) => {
    // Active drag wins over everything (it's the in-progress user gesture).
    if (resizePreview && s.id === resizePreview.sessionId) {
      return { startTime: resizePreview.startTime, endTime: resizePreview.endTime };
    }
    if (movePreview && s.id === movePreview.sessionId) {
      return { startTime: movePreview.startTime, endTime: movePreview.endTime };
    }
    // Per-date 'modified' override: if there's an exception for this rule on
    // the viewed week's date, use its override times. The rule itself stays
    // unchanged so other weeks keep showing the original times.
    const dateStr = getDateForWeekday(s.dayOfWeek);
    const ex = localExceptions.find(
      e => e.sessionId === s.id && e.date === dateStr && e.status === 'modified',
    );
    if (ex && ex.overrideStartTime && ex.overrideEndTime) {
      return { startTime: ex.overrideStartTime, endTime: ex.overrideEndTime };
    }
    return { startTime: s.startTime, endTime: s.endTime };
  };

  // Display-lane resolution: a session keeps its stored lane as a starting bid,
  // but gets bumped to a higher lane when its effective time range overlaps
  // another session already placed on that lane. Keeps multi-child views
  // overlap-safe without mutating stored state.
  const displayLanes = (() => {
    const map = new Map<string, number>();
    const byDay = new Map<string, ScheduleSession[]>();
    for (const s of viewSessions) {
      if (!isSessionVisible(s)) continue;
      const list = byDay.get(s.dayOfWeek);
      if (list) list.push(s);
      else byDay.set(s.dayOfWeek, [s]);
    }
    byDay.forEach((list) => {
      const sorted = [...list].sort((a, b) => {
        if (a.lane !== b.lane) return a.lane - b.lane;
        const aStart = timeToMins(getEffectiveTimes(a).startTime);
        const bStart = timeToMins(getEffectiveTimes(b).startTime);
        if (aStart !== bStart) return aStart - bStart;
        return a.id.localeCompare(b.id);
      });
      const occupied: Array<Array<{ start: number; end: number }>> = [];
      for (const s of sorted) {
        const { startTime, endTime } = getEffectiveTimes(s);
        const sStart = timeToMins(startTime);
        const sEnd = timeToMins(endTime);
        let lane = Math.max(0, s.lane);
        // eslint-disable-next-line no-constant-condition
        while (true) {
          if (!occupied[lane]) occupied[lane] = [];
          const clear = occupied[lane].every(r => sEnd <= r.start || sStart >= r.end);
          if (clear) {
            occupied[lane].push({ start: sStart, end: sEnd });
            map.set(s.id, lane);
            break;
          }
          lane++;
        }
      }
    });
    return map;
  })();

  const getDisplayLane = (s: ScheduleSession): number =>
    displayLanes.get(s.id) ?? s.lane;

  const getSessionAt = (day: string, lane: number, slotIdx: number): ScheduleSession | null => {
    if (slotIdx < 0 || slotIdx >= slots.length) return null;
    const slotMins = timeToMins(slots[slotIdx].start);
    for (const s of viewSessions) {
      if (s.dayOfWeek !== day || getDisplayLane(s) !== lane) continue;
      if (!isSessionVisible(s)) continue;
      const { startTime, endTime } = getEffectiveTimes(s);
      if (slotMins >= timeToMins(startTime) && slotMins < timeToMins(endTime)) {
        return s;
      }
    }
    return null;
  };

  const isFirstSlot = (s: ScheduleSession, slotIdx: number): boolean => {
    const { startTime } = getEffectiveTimes(s);
    return timeToMins(slots[slotIdx].start) === timeToMins(startTime);
  };

  const isLastSlot = (s: ScheduleSession, slotIdx: number): boolean => {
    const { endTime } = getEffectiveTimes(s);
    return timeToMins(slots[slotIdx].end) === timeToMins(endTime);
  };

  const getSessionColour = (s: ScheduleSession): string => {
    if (s.childId) {
      const child = childProfiles.find(c => c.id === s.childId);
      return child?.colour_code || '#aaaaaa';
    }
    return '#aaaaaa';
  };

  const getSessionInitials = (s: ScheduleSession): string => {
    if (s.childId) {
      const child = childProfiles.find(c => c.id === s.childId);
      return child?.initials || '';
    }
    return '';
  };

  const getLaneCount = (day: string): number => {
    let maxLane = -1;
    for (const s of viewSessions) {
      if (s.dayOfWeek !== day) continue;
      if (!isSessionVisible(s)) continue;
      const dl = getDisplayLane(s);
      if (dl > maxLane) maxLane = dl;
    }
    if (draftBounds && draftBounds.day === day && draftBounds.lane > maxLane) {
      maxLane = draftBounds.lane;
    }
    return Math.max(MIN_LANES, maxLane + 2);
  };

  // ── Conflict detection ─────────────────────────────────────
  // The rules live in scheduleRules; these bind them to the grid's current
  // sessions and exceptions, so call sites read exactly what they did.
  const clashCtx: ClashContext = { sessions, exceptions: localExceptions };
  const findChildConflicts = (candidate: ScheduleSession, excludeId: string, ignoreCandidateStoredOverrides = false) =>
    rules.findChildConflicts(clashCtx, candidate, excludeId, ignoreCandidateStoredOverrides);
  const findChildConflictsOnDate = (
    candidate: ScheduleSession,
    excludeId: string,
    dateStr: string,
    times: { startTime: string; endTime: string },
  ) => rules.findChildConflictsOnDate(clashCtx, candidate, excludeId, dateStr, times);
  const findTeacherConflicts = (candidate: ScheduleSession, excludeId: string) =>
    rules.findTeacherConflicts(clashCtx, candidate, excludeId);

  // Compact date list for conflict banners (dates are already capped at 8).
  const formatConflictDates = (dates: string[]): string => {
    const uniq = Array.from(new Set(dates)).sort();
    if (uniq.length === 0) return '';
    if (uniq.length <= 3) return uniq.map(formatDisplayDate).join(', ');
    return `${uniq.slice(0, 3).map(formatDisplayDate).join(', ')} and more`;
  };

  const describeChildConflict = (candidate: ScheduleSession, conflicts: SessionConflict[]): string => {
    const child = candidate.childId != null
      ? childProfiles.find(c => c.id === candidate.childId)
      : null;
    const who = child ? `${child.first_name} ${child.last_name}` : 'this child';
    const dates = conflicts.flatMap(c => c.dates);
    return `This overlaps another session for ${who} on ${formatConflictDates(dates)}. ` +
      `A child can't be in two sessions at once — move or shorten one of them first.`;
  };

  const describeTeacherConflict = (candidate: ScheduleSession, conflicts: SessionConflict[]): string => {
    const ids = new Set<number>();
    conflicts.forEach(c => c.session.teacherIds.forEach(t => {
      if (candidate.teacherIds.includes(t)) ids.add(t);
    }));
    const names = Array.from(ids)
      .map(id => editorStaff.find(s => s.id === id)?.name)
      .filter((n): n is string => !!n);
    const who = names.length ? names.join(', ') : 'A teacher';
    const dates = conflicts.flatMap(c => c.dates);
    return `Heads up: ${who} is also assigned to another session at this time on ${formatConflictDates(dates)}.`;
  };

  // ── Drag handlers ──────────────────────────────────────────

  const handleCellMouseDown = async (e: React.MouseEvent, day: string, lane: number, idx: number) => {
    e.preventDefault();
    const session = getSessionAt(day, lane, idx);

    if (session) {
      // Click on existing session → select it (stop propagation to prevent deselect)
      e.stopPropagation();

      // Ctrl/Cmd-click toggles the clicked session in/out of the multi-select
      // pool without disturbing the primary selection (or other extras).
      // We don't touch fresh-mode bookkeeping here — bulk-edit deliberately
      // leaves each session's own state alone; only bulk operations apply.
      if (e.ctrlKey || e.metaKey) {
        // Block multi-select if there's a dirty draft on the primary — the
        // user would lose context if they navigate. Force them to resolve first.
        if (draftEdits) {
          // Treat as a normal swap-click — let the existing discard prompt fire.
        } else {
          if (session.id === selectedSessionId) {
            // Demote the primary; promote one of the extras (if any) to primary.
            const next = new Set(extraSelectedIds);
            const promoted = next.values().next().value;
            if (promoted !== undefined) {
              next.delete(promoted);
              setExtraSelectedIds(next);
              setSelectedSessionId(promoted);
            } else {
              setExtraSelectedIds(new Set());
              setSelectedSessionId(null);
              setPanelOpen(false);
            }
            return;
          }
          if (extraSelectedIds.has(session.id)) {
            const next = new Set(extraSelectedIds);
            next.delete(session.id);
            setExtraSelectedIds(next);
            return;
          }
          // Add to selection without disturbing the primary.
          const next = new Set(extraSelectedIds);
          next.add(session.id);
          setExtraSelectedIds(next);
          // Ensure the panel is visible so the bulk-edit UI shows.
          setPanelOpen(true);
          return;
        }
      }

      // Plain click on a session — clear any multi-select extras so we land
      // back in single-edit mode on the freshly-clicked session.
      if (extraSelectedIds.size > 0) setExtraSelectedIds(new Set());
      // No drag while the panel holds unsaved edits: a press on another bar
      // stops to ask about them (the button is long released by the time
      // it's answered), and on this bar the drop would save against a rule
      // the panel is still mid-way through changing.
      const hasUnsavedDraft = draftEdits !== null;

      // If a draft is in flight on the previously-selected session, confirm before switching.
      // Guard on selectedSessionId being non-null — without it, an orphan draft
      // (from a deselected session) triggers a spurious prompt when the user
      // clicks any session bar.
      if (draftEdits && selectedSessionId !== null && selectedSessionId !== session.id) {
        const ok = await confirmAction({
          title: 'Discard unsaved changes?',
          message: 'You have unsaved changes on the previous session. Discard them and switch?',
          confirmText: 'Discard',
        });
        if (!ok) {
          dragTypeRef.current = 'none';
          return;
        }
        setDraftEdits(null);
        setDraftSnapshot(null);
      }
      // Settle the previously-selected fresh session so it transitions out of fresh mode.
      // If it never got persisted (still no serverId), drop it as an abandoned create.
      if (selectedSessionId && selectedSessionId !== session.id) {
        const prevSession = sessions.find(s => s.id === selectedSessionId);
        if (prevSession && freshSessionIds.has(selectedSessionId) && prevSession.serverId === undefined) {
          removeSessionLocally(selectedSessionId);
        }
        setFreshSessionIds(prev => {
          if (!prev.has(selectedSessionId)) return prev;
          const next = new Set(prev);
          next.delete(selectedSessionId);
          return next;
        });
      }
      // Clicking on a session's bar is a deliberate gesture that means "I'm
      // re-interacting with this existing session" — settle it out of fresh
      // mode regardless of whether it was the previously-selected one. This
      // is what surfaces the Save button after a re-click.
      if (freshSessionIds.has(session.id)) {
        setFreshSessionIds(prev => {
          const next = new Set(prev);
          next.delete(session.id);
          return next;
        });
      }
      setSelectedSessionId(session.id);
      dragTypeRef.current = 'none';

      // Unlocked: the press may become a move. It stays a plain select unless
      // the pointer reaches another cell (see handleCellMouseEnter). A
      // cancelled date has nothing to move — restore it first.
      //
      // The panel then opens on release, not here: opening it narrows the
      // grid (and collapses the nav), which would slide the timeline out
      // from under a pointer that's mid-drag.
      if (!gridLocked && !hasUnsavedDraft && !isSessionCancelledThisWeek(session)) {
        dragTypeRef.current = 'move-pending';
        dragMoveSessionRef.current = session.id;
        dragMoveGrabOffsetRef.current =
          rules.moveGrabOffset(slots[idx], getEffectiveTimes(session).startTime);
      } else {
        setPanelOpen(true);
      }
    } else {
      // Click on empty → deselect, start create-drag. Dragging is allowed on any
      // lane; on release, the session snaps to the first overlap-free lane.
      // No "block when filter=All" check — the panel handles the no-child case
      // via its first-time-create branch (see SCHEDULE_EDITING_UX.md).
      e.stopPropagation();

      // Lock guard: a single tap just flashes the lock toggle; a double-click,
      // 2 clicks within 3s, or click-and-drag opens the unlock modal.
      if (gridLocked) {
        dragTypeRef.current = 'none';
        // Empty-cell click on a locked grid still deselects so the user has
        // a way to dismiss the side panel without unlocking first.
        setSelectedSessionId(null);
        if (extraSelectedIds.size > 0) setExtraSelectedIds(new Set());
        setPanelOpen(false);
        lockedClickGuard(e);
        return;
      }

      // Abandon the previous session if it was a fresh-create that never got
      // a child (and so was never persisted). Avoids leaving orphan empty
      // sessions in the grid as the user drags new ones.
      if (selectedSessionId) {
        const prev = sessions.find(s => s.id === selectedSessionId);
        if (prev && freshSessionIds.has(selectedSessionId) && prev.serverId === undefined) {
          removeSessionLocally(selectedSessionId);
          setFreshSessionIds(prevSet => {
            if (!prevSet.has(selectedSessionId)) return prevSet;
            const next = new Set(prevSet);
            next.delete(selectedSessionId);
            return next;
          });
        }
      }

      setSelectedSessionId(null);
      if (extraSelectedIds.size > 0) setExtraSelectedIds(new Set());
      dragTypeRef.current = 'create';
      dragDayRef.current = day;
      dragLaneRef.current = lane;
      dragStartIdxRef.current = idx;
      setDraftBounds({ day, lane, startIdx: idx, endIdx: idx });
    }
  };

  const handleResizeMouseDown = (e: React.MouseEvent, sessionId: string, direction: 'left' | 'right') => {
    e.preventDefault();
    e.stopPropagation();
    dragTypeRef.current = direction === 'left' ? 'resize-left' : 'resize-right';
    dragResizeSessionRef.current = sessionId;
    const session = sessions.find(s => s.id === sessionId);
    if (session) {
      // Seed from the *effective* times — what the bar is actually drawn from
      // — not the rule's. On a date carrying a 'modified' override those
      // differ, and since getEffectiveTimes gives an active drag priority over
      // the override, seeding from the rule makes the bar jump to the
      // underlying entry the instant the handle is grabbed.
      const { startTime, endTime } = getEffectiveTimes(session);
      setResizePreview({ sessionId, startTime, endTime });
    }
  };

  const handleCellMouseEnter = (day: string, lane: number, idx: number) => {
    setHoveredCell(`${day}:${lane}:${idx}`);

    if (dragTypeRef.current === 'create') {
      if (day !== dragDayRef.current || lane !== dragLaneRef.current) return;
      setDraftBounds(prev => prev ? { ...prev, endIdx: idx } : null);

    } else if (dragTypeRef.current === 'resize-left' || dragTypeRef.current === 'resize-right') {
      const session = sessions.find(s => s.id === dragResizeSessionRef.current);
      if (!session) return;
      // Only react to same day
      if (day !== session.dayOfWeek) return;

      // getEffectiveTimes already prefers the in-flight resizePreview, falling
      // back to any per-date override and only then the rule — so it's the
      // right source for the edge we're *not* dragging.
      if (dragTypeRef.current === 'resize-left') {
        const newStart = slots[idx]?.start;
        if (!newStart) return;
        const effEnd = getEffectiveTimes(session).endTime;
        if (timeToMins(newStart) < timeToMins(effEnd)) {
          setResizePreview({ sessionId: session.id, startTime: newStart, endTime: effEnd });
        }
      } else {
        const newEnd = slots[idx]?.end;
        if (!newEnd) return;
        const effStart = getEffectiveTimes(session).startTime;
        if (timeToMins(newEnd) > timeToMins(effStart)) {
          setResizePreview({ sessionId: session.id, startTime: effStart, endTime: newEnd });
        }
      }

    } else if (dragTypeRef.current === 'move-pending' || dragTypeRef.current === 'move') {
      const session = sessions.find(s => s.id === dragMoveSessionRef.current);
      if (!session || !slots[idx] || slots.length === 0) return;
      // getEffectiveTimes prefers the in-flight preview; either way the
      // duration is the bar's own, which a move always keeps.
      const { startTime, endTime } = getEffectiveTimes(session);
      const next: MovePreview = {
        sessionId: session.id,
        day,
        ...rules.moveTimesFor({ slots, idx, grabOffset: dragMoveGrabOffsetRef.current, startTime, endTime }),
      };
      if (dragTypeRef.current === 'move-pending') {
        // Still on its own spot → still just a click.
        if (day === session.dayOfWeek && next.startTime === startTime) return;
        dragTypeRef.current = 'move';
      }
      movePreviewRef.current = next;
      setMovePreview(prev => (
        prev && prev.day === next.day && prev.startTime === next.startTime ? prev : next
      ));
    }
  };

  const handleMouseUp = () => {
    if (dragTypeRef.current === 'create' && draftBounds) {
      const lo = Math.min(draftBounds.startIdx, draftBounds.endIdx);
      const hi = Math.max(draftBounds.startIdx, draftBounds.endIdx);
      if (lo >= 0 && hi < slots.length) {
        // Snap to the first overlap-free display lane, regardless of which
        // lane the user was dragging on. Compares against currently visible
        // sessions' effective positions.
        const startMins = timeToMins(slots[lo].start);
        // Single-cell click (no drag) → the session gets the default length
        // from the defaults modal, clamped to the working day. A drag sizes
        // by grid slots as before.
        const isClick = lo === hi;
        const endMins = isClick
          ? Math.min(startMins + defaultSessionLengthMinutes, timeToMins(dayEndTime))
          : timeToMins(slots[hi].end);
        const occupied = new Map<number, Array<{ start: number; end: number }>>();
        for (const s of sessions) {
          if (s.dayOfWeek !== draftBounds.day) continue;
          if (!isSessionVisible(s)) continue;
          const dl = getDisplayLane(s);
          const { startTime, endTime } = getEffectiveTimes(s);
          const list = occupied.get(dl) ?? [];
          list.push({ start: timeToMins(startTime), end: timeToMins(endTime) });
          occupied.set(dl, list);
        }
        let freeLane = 0;
        while (true) {
          const ranges = occupied.get(freeLane) ?? [];
          const clash = ranges.some(r => !(endMins <= r.start || startMins >= r.end));
          if (!clash) break;
          freeLane++;
        }

        // Default start date: the actual calendar date of draftBounds.day in
        // the currently-viewed week. Falls back to this week's Monday + offset.
        const baseMonday = weekStart
          ? new Date(weekStart + 'T00:00')
          : getMonday(new Date());
        baseMonday.setDate(baseMonday.getDate() + (DAY_OFFSETS[draftBounds.day] ?? 0));
        const defaultStartDate = toDateString(baseMonday);

        const newSession: ScheduleSession = {
          id: `session-${nextSessionId.current++}`,
          dayOfWeek: draftBounds.day,
          lane: freeLane,
          startTime: slots[lo].start,
          endTime: minsToTime(endMins),
          childId: panelDefaults.childId,
          teacherIds: [...panelDefaults.teacherIds],
          locationIds: [...panelDefaults.locationIds],
          frequency: 'weekly',
          startDate: defaultStartDate,
          endDate: null,
        };
        setSessions(prev => [...prev, newSession]);
        setSelectedSessionId(newSession.id);
        setPanelOpen(true);
        setFreshSessionIds(prev => {
          const next = new Set(prev);
          next.add(newSession.id);
          return next;
        });

        // Auto-save the new rule only when we have a child to attach it to,
        // and only when it doesn't double-book that child. On a clash we leave
        // the session as an unsaved fresh draft so the panel banner can show
        // the conflict and the user can adjust the time/day or pick a different
        // child before it persists. If childId is null, the panel requires the
        // user to pick one and uses the explicit Save path.
        const createClashes = newSession.childId !== null
          && findChildConflicts(newSession, newSession.id).length > 0;
        if (newSession.childId !== null && !createClashes) {
          const { id: _localId, lane: _lane, serverId: _sid, ...rulePayload } = newSession;
          const promise = ScheduleDataService.createScheduleItem(rulePayload);
          createPromisesRef.current.set(newSession.id, promise);
          promise.then(({ id }) => {
            // Set the ref FIRST so handleSessionUpdate calls firing in the
            // same microtask cycle (before React re-renders) see the new id.
            sessionServerIdsRef.current.set(newSession.id, id);
            createPromisesRef.current.delete(newSession.id);
            setSessions(prev =>
              prev.map(s => (s.id === newSession.id ? { ...s, serverId: id } : s)),
            );
            // Snapshot defaults for the next new session from the just-saved one.
            snapshotDefaultsFromSave({
              childId: selectedChildIdNum ?? newSession.childId,
              teacherIds: newSession.teacherIds,
              locationIds: newSession.locationIds,
            });
          }).catch(err => {
            createPromisesRef.current.delete(newSession.id);
            // eslint-disable-next-line no-console
            console.error('Failed to persist new schedule_item', err);
          });
        }
      }
      setDraftBounds(null);

    } else if (dragTypeRef.current === 'move' && movePreviewRef.current) {
      // The ref, not the state: a release straight after the last move can
      // arrive before React has re-rendered with it, and this closure's
      // movePreview would drop the bar one step short.
      const drop = movePreviewRef.current;
      movePreviewRef.current = null;
      setMovePreview(null);
      dragTypeRef.current = 'none';
      setPanelOpen(true);
      commitMove(drop);
      return;

    } else if (dragTypeRef.current === 'move') {
      // A move with no preview was released off the grid, which cleared it
      // (the grid's onMouseLeave) — so the move is called off, not dropped.
      abandonMove();
      return;

    } else if (dragTypeRef.current === 'move-pending') {
      // Pressed and released without moving — a plain select, whose panel
      // was held back until now (see handleCellMouseDown).
      setPanelOpen(true);

    } else if (
      (dragTypeRef.current === 'resize-left' || dragTypeRef.current === 'resize-right') &&
      resizePreview
    ) {
      const target = sessions.find(s => s.id === resizePreview.sessionId);
      const newTimes = { startTime: resizePreview.startTime, endTime: resizePreview.endTime };

      // Hard block: refuse a resize that would double-book the child. Revert
      // by discarding the preview without applying, and surface the clash via
      // the standard info dialog (same pattern as the attendance-block guard).
      //
      // Scoped to the edited date only. The scope choice ("just this date" vs
      // "from now on") hasn't been made yet, but the edited date takes the new
      // times under BOTH — so a clash here invalidates the resize either way,
      // and it's the only date we can fairly judge before the user chooses.
      // The rule-level question is asked later, in the 'persist' branch.
      const resizeDate = target ? getDateForWeekday(target.dayOfWeek) : '';
      const resizeConflicts = target
        ? findChildConflictsOnDate(target, target.id, resizeDate, newTimes)
        : [];
      if (target && resizeConflicts.length > 0) {
        const msg = describeChildConflict({ ...target, ...newTimes }, resizeConflicts);
        setResizePreview(null);
        dragTypeRef.current = 'none';
        confirmAction({
          title: "Sessions can't overlap",
          message: msg,
          confirmText: 'OK',
          hideCancel: true,
        });
        return;
      }

      // The date already carries an override, so the scope question has no
      // second answer — the bar being dragged IS the single-date exception.
      // Write it straight through and leave the rule alone, exactly as the
      // panel's Save does for an override-only edit.
      const overrideForDate = target
        ? localExceptions.find(
          e => e.sessionId === target.id && e.date === resizeDate && e.status === 'modified',
        )
        : undefined;
      if (target && overrideForDate) {
        void commitOverrideEditGuarded(overrideForDate, newTimes);
        setResizePreview(null);
        dragTypeRef.current = 'none';
        return;
      }

      // Apply the resize locally either way so the user sees the result.
      setSessions(prev =>
        prev.map(s => (s.id === resizePreview.sessionId ? { ...s, ...newTimes } : s)),
      );

      // For a persisted rule, the user must pick a scope before the change is committed.
      if (target?.serverId !== undefined) {
        setScopeError(null);
        setPendingEdit({
          sessionId: target.id,
          kind: 'resize',
          snapshot: target,
          appliedUpdate: newTimes,
          editDate: getDateForWeekday(target.dayOfWeek),
        });
      }

      setResizePreview(null);
    }

    dragTypeRef.current = 'none';
  };

  // Drop of a dragged bar: same length, possibly a new time and/or day.
  //   - never saved      → moves straight away (fresh-session edit)
  //   - same day, the date already has a time change → that change is updated
  //   - one-off          → moves in place, no scope question (one date only)
  //   - repeating        → scope modal; "Just this date" for a new day is a
  //                        cancellation plus a one-off (handleScopeChoice)
  // Carries out what rules.decideDrop says a drop means.
  const commitMove = (drop: MovePreview) => {
    const target = sessions.find(s => s.id === drop.sessionId);
    if (!target) return;
    const decision = rules.decideDrop(
      clashCtx, target, drop,
      getDateForWeekday(target.dayOfWeek), getDateForWeekday(drop.day),
    );
    switch (decision.type) {
      case 'none':
        return;
      case 'clash':
        confirmAction({
          title: "Sessions can't overlap",
          message: describeChildConflict({ ...target, ...decision.times }, decision.conflicts),
          confirmText: 'OK',
          hideCancel: true,
        });
        return;
      case 'fresh':
        // Never saved — the press selected it, so this is an ordinary live edit.
        handleSessionUpdate(decision.updates);
        return;
      case 'override':
        void commitOverrideEditGuarded(decision.override, decision.times);
        return;
      case 'scope': {
        const edit: PendingEdit = {
          sessionId: target.id,
          kind: decision.kind,
          snapshot: target,
          appliedUpdate: decision.appliedUpdate,
          editDate: decision.editDate,
          persistOnly: decision.persistOnly,
        };
        setSessions(prev => prev.map(s => (s.id === target.id ? { ...s, ...decision.appliedUpdate } : s)));
        setScopeError(null);
        setPendingEdit(edit);
        if (decision.persistOnly) void handleScopeChoice('persist', edit);
      }
    }
  };

  // ── Session CRUD for detail panel ──────────────────────────

  const baseSelectedSession = sessions.find(s => s.id === selectedSessionId) ?? null;
  const selectedEditDate = baseSelectedSession
    ? getDateForWeekday(baseSelectedSession.dayOfWeek)
    : null;

  // The 'modified' override in force on the viewed date, if any.
  const selectedOverride = baseSelectedSession && selectedEditDate
    ? localExceptions.find(
        e => e.sessionId === baseSelectedSession.id &&
             e.date === selectedEditDate &&
             e.status === 'modified',
      ) ?? null
    : null;

  // What that override actually says, as a session-shaped partial. Only the
  // fields schedule_exception has columns (or bridges) for can appear here —
  // child, frequency and the date range are rule-level by construction.
  const overrideValues: Partial<ScheduleSession> = selectedOverride
    ? {
      ...(selectedOverride.overrideStartTime && selectedOverride.overrideEndTime
        ? {
          startTime: selectedOverride.overrideStartTime,
          endTime: selectedOverride.overrideEndTime,
        }
        : {}),
      ...(selectedOverride.overrideTeacherIds
        ? { teacherIds: selectedOverride.overrideTeacherIds }
        : {}),
      ...(selectedOverride.overrideLocationIds
        ? { locationIds: selectedOverride.overrideLocationIds }
        : {}),
    }
    : {};

  // Overlay any draft edits onto the session for display, so the panel renders
  // pending changes before the user commits via Save changes.
  //
  // NB two different sessions below, deliberately:
  //   selectedSession — rule + drafts. The RULE's own values, so rule-level
  //     questions (clash across every occurrence) get asked about the rule.
  //   panelSession    — rule + this date's override + drafts. What the user is
  //     actually looking at on the viewed date, so it's what the panel renders.
  const selectedSession = baseSelectedSession && draftEdits
    ? { ...baseSelectedSession, ...draftEdits }
    : baseSelectedSession;
  const panelSession = baseSelectedSession
    ? { ...baseSelectedSession, ...overrideValues, ...(draftEdits ?? {}) }
    : null;
  const isSelectedFresh = selectedSessionId
    ? freshSessionIds.has(selectedSessionId) || baseSelectedSession?.serverId === undefined
    : true;
  const isPanelDirty = draftEdits !== null;

  // Whether the panel takes up space. Not panelOpen alone: plenty of paths
  // clear the selection without closing it, which left an empty panel holding
  // its width. It takes space only while it has something to show — or while
  // a new session is being drawn, so the grid doesn't resize mid-drag (the
  // press cleared the selection; the release fills the panel again).
  const panelShown = panelOpen && (isMultiSelect || !!panelSession || draftBounds !== null);
  // And a panel with nothing to show is closed, not just hidden: left open, it
  // would spring back the moment anything was selected — including on a press
  // that's about to become a move, whose panel waits for the release.
  useEffect(() => {
    if (panelOpen && !panelShown) setPanelOpen(false);
  }, [panelOpen, panelShown]);

  // Collapse the left nav when the session detail panel opens. Deliberately
  // one-way: closing the panel doesn't restore it, and nor does arriving here
  // with no panel open — the menu only ever reopens when the user pins it.
  useEffect(() => {
    if (panelShown) setSidebarOpen(false);
  }, [panelShown, setSidebarOpen]);

  // Fields an exception can carry. An edit confined to these on an already-
  // overridden date needs no scope question — it can only mean "this date".
  const OVERRIDABLE_FIELDS: Array<keyof ScheduleSession> =
    ['startTime', 'endTime', 'teacherIds', 'locationIds'];
  // When and how often the rule runs — nothing a single date can override.
  const RULE_ONLY_FIELDS: Array<keyof ScheduleSession> =
    ['startDate', 'endDate', 'frequency', 'dayOfWeek'];
  const draftTouchesOnlyOverridable = draftEdits !== null
    && Object.keys(draftEdits).length > 0
    && Object.keys(draftEdits).every(
      k => OVERRIDABLE_FIELDS.includes(k as keyof ScheduleSession),
    );
  // True when Save should write straight to the exception rather than opening
  // the scope modal.
  const savingOverrideOnly = selectedOverride !== null && draftTouchesOnlyOverridable;

  // The child new sessions will inherit — shown in the "Next new session"
  // pill and (read-only) in the defaults modal.
  const defaultChildProfile = panelDefaults.childId != null
    ? childProfiles.find(c => c.id === panelDefaults.childId) ?? null
    : null;

  // Live conflict checks for the selected (draft-merged) session, driving the
  // panel banners and the Save gate. Child overlaps are a hard block; teacher
  // overlaps are a soft heads-up.
  // An override-only edit changes exactly one date, so it gets the one-date
  // question. Anything else is a rule-level edit and gets the rule-level one.
  const selectedChildConflicts = savingOverrideOnly && panelSession && selectedEditDate
    ? findChildConflictsOnDate(
      panelSession, panelSession.id, selectedEditDate,
      { startTime: panelSession.startTime, endTime: panelSession.endTime },
    )
    : selectedSession
      ? findChildConflicts(selectedSession, selectedSession.id)
      : [];
  const selectedTeacherConflicts = selectedSession
    ? findTeacherConflicts(selectedSession, selectedSession.id)
    : [];
  const conflictSubject = savingOverrideOnly ? panelSession : selectedSession;
  const selectedConflictError = conflictSubject && selectedChildConflicts.length > 0
    ? describeChildConflict(conflictSubject, selectedChildConflicts)
    : null;
  const selectedConflictWarning = selectedSession && selectedTeacherConflicts.length > 0
    ? describeTeacherConflict(selectedSession, selectedTeacherConflicts)
    : null;

  const isSelectedCancelledThisWeek = baseSelectedSession
    ? isSessionCancelledThisWeek(baseSelectedSession)
    : false;
  // A cancelled date already holds a 'cancelled' exception, and the
  // (schedule_item_id, exception_date) pair is UNIQUE — so a one-off edit here
  // could only land by rewriting that row into a 'modified' one, silently
  // un-cancelling the date. Block it instead: the panel's banner offers
  // Restore, and the grid already hides the resize handles on cancelled bars.
  const canSavePanel =
    !!selectedSession && selectedSession.childId !== null && selectedConflictError === null
    && !isSelectedCancelledThisWeek;
  // Reason captured at cancellation time, surfaced on the panel banner.
  const selectedCancelledReason: string | null = (() => {
    if (!isSelectedCancelledThisWeek || !baseSelectedSession || !selectedEditDate) return null;
    const ex = localExceptions.find(
      e => e.sessionId === baseSelectedSession.id
        && e.date === selectedEditDate
        && e.status === 'cancelled',
    );
    return ex?.reason ?? null;
  })();

  // Settled-session destructive actions. These bypass the scope-choice modal
  // because their two intents (cancel-one-date vs. end-date-forwards) are
  // distinct enough to be exposed as separate buttons.
  // Side-panel "Cancel for this date" → open the reason modal first.
  const handleCancelForDate = () => {
    if (!baseSelectedSession || !selectedEditDate) return;
    setCancelDateError(null);
    setCancelDateModalOpen(true);
  };

  // Modal confirm — actually writes the cancellation exception with the
  // captured reason (or null if the user skipped the picker). Gated by the
  // attendance-block guard: a one-off cancel only affects the chosen date,
  // so we check that exact date.
  const submitCancelForDate = async (reason: string | null) => {
    if (!baseSelectedSession || !selectedEditDate) return;
    const sessionId = baseSelectedSession.id;
    const exceptionDate = selectedEditDate;
    if (
      baseSelectedSession.serverId !== undefined &&
      baseSelectedSession.childId !== null
    ) {
      const blockers = await hasBlockingAttendance(
        baseSelectedSession.serverId,
        baseSelectedSession.childId,
        exceptionDate,
        exceptionDate,
      );
      if (blockers.length > 0) {
        setCancelDateError(buildBlockMessage('cancel', blockers));
        return;
      }
    }
    setCancelDateError(null);
    setCancelDateModalOpen(false);

    // If the date is already overridden, it holds a 'modified' row and the
    // (item, date) pair is UNIQUE — so this is a conversion, not a create.
    // POST /schedule/exceptions/bulk does exactly this upsert for the bulk
    // path (status → 'cancelled', override times nulled, bridges dropped);
    // this brings the single-date path in line rather than 409-ing.
    const overrideToConvert = localExceptions.find(
      e => e.sessionId === sessionId && e.date === exceptionDate && e.status === 'modified',
    );
    const overrideServerId = overrideToConvert?.serverId;
    if (overrideToConvert !== undefined && overrideServerId !== undefined) {
      const previous = overrideToConvert;
      const serverId = overrideServerId;
      setLocalExceptions(prev => prev.map(ex =>
        ex === previous
          ? { sessionId, date: exceptionDate, status: 'cancelled', serverId, reason }
          : ex,
      ));
      ScheduleDataService.updateScheduleException(serverId, {
        status: 'cancelled',
        overrideStartTime: null,
        overrideEndTime: null,
        teacherIds: [],
        locationIds: [],
        reason,
      }).catch(err => {
        setLocalExceptions(prev => prev.map(ex =>
          ex.serverId === serverId ? previous : ex,
        ));
        // eslint-disable-next-line no-console
        console.error('Failed to convert override to a cancellation', err);
        reportExceptionWriteFailure('cancel this date', err);
      });
      return;
    }

    // Optimistically add the local exception so the bar greys out immediately;
    // patch in the serverId when the POST resolves.
    setLocalExceptions(prev => [
      ...prev,
      { sessionId, date: exceptionDate, status: 'cancelled', reason },
    ]);
    if (baseSelectedSession.serverId !== undefined) {
      ScheduleDataService.createScheduleException({
        scheduleItemServerId: baseSelectedSession.serverId,
        exceptionDate,
        status: 'cancelled',
        reason: reason ?? undefined,
      }).then(({ id }) => {
        setLocalExceptions(prev =>
          prev.map(ex =>
            ex.sessionId === sessionId && ex.date === exceptionDate && ex.status === 'cancelled' && ex.serverId === undefined
              ? { ...ex, serverId: id }
              : ex,
          ),
        );
      }).catch(err => {
        // Roll back the optimistic add on failure.
        setLocalExceptions(prev =>
          prev.filter(ex =>
            !(ex.sessionId === sessionId && ex.date === exceptionDate && ex.status === 'cancelled' && ex.serverId === undefined),
          ),
        );
        // eslint-disable-next-line no-console
        console.error('Failed to write cancellation exception', err);
        reportExceptionWriteFailure('cancel this date', err);
      });
    }
  };

  const handleDeleteFromDate = async () => {
    if (!baseSelectedSession || !selectedEditDate) return;
    // Attendance-block guard: deleting from a date forwards affects that
    // date and every future occurrence, so block on any attendance record
    // in [selectedEditDate, ∞]. Surface the block via a single-button
    // info dialog so the user can acknowledge and clear attendance first.
    if (
      baseSelectedSession.serverId !== undefined &&
      baseSelectedSession.childId !== null
    ) {
      const blockers = await hasBlockingAttendance(
        baseSelectedSession.serverId,
        baseSelectedSession.childId,
        selectedEditDate,
      );
      if (blockers.length > 0) {
        await confirmAction({
          title: "Can't delete from this date",
          message: buildBlockMessage('delete', blockers),
          confirmText: 'OK',
          hideCancel: true,
          // Blocked with no way forward on this screen — the guide is the way out.
          help: { guideKey: 'attendance.remove-record', label: 'How do I remove an attendance record?' },
        });
        return;
      }
    }
    const ok = await confirmAction({
      title: 'Delete from this date forwards?',
      message: `Past dates will keep their existing entries. ${selectedEditDate} and future occurrences will no longer appear.`,
      confirmText: 'Delete',
      isDeleting: true,
    });
    if (!ok) return;
    const newEndDate = addDays(selectedEditDate, -1);
    const sessionId = baseSelectedSession.id;
    const serverId = baseSelectedSession.serverId;
    // If the would-be end-date is before the rule's start_date, end-dating
    // would violate the DB CHECK and there's no past to preserve anyway —
    // hard-delete the rule instead.
    const wouldOrphanHistory = newEndDate < baseSelectedSession.startDate;

    if (serverId === undefined) {
      // Local-only — just remove.
      removeSessionLocally(sessionId);
      setSelectedSessionId(null);
      setPanelOpen(false);
      return;
    }

    if (wouldOrphanHistory) {
      ScheduleDataService.deleteScheduleItem(serverId)
        .then(() => {
          removeSessionLocally(sessionId);
          setSelectedSessionId(null);
          setPanelOpen(false);
        })
        .catch(err => {
          // eslint-disable-next-line no-console
          console.error('Failed to hard-delete schedule_item', err);
        });
    } else {
      ScheduleDataService.endDateScheduleItem(serverId, newEndDate)
        .then(() => {
          setSessions(prev =>
            prev.map(s => (s.id === sessionId ? { ...s, endDate: newEndDate } : s)),
          );
          setSelectedSessionId(null);
          setPanelOpen(false);
        })
        .catch(err => {
          // eslint-disable-next-line no-console
          console.error('Failed to end-date schedule_item', err);
        });
    }
  };

  // Restore a previously-cancelled session for the currently-viewed date.
  const handleSessionRestore = () => {
    if (!baseSelectedSession || !selectedEditDate) return;
    const target = localExceptions.find(
      ex => ex.sessionId === baseSelectedSession.id &&
            ex.date === selectedEditDate &&
            ex.status === 'cancelled',
    );
    if (target?.serverId !== undefined) {
      ScheduleDataService.deleteScheduleException(target.serverId).catch(err => {
        // eslint-disable-next-line no-console
        console.error('Failed to delete cancellation exception', err);
      });
    }
    setLocalExceptions(prev =>
      prev.filter(
        ex =>
          !(ex.sessionId === baseSelectedSession.id &&
            ex.date === selectedEditDate &&
            ex.status === 'cancelled'),
      ),
    );
  };

  // ── Bulk-edit handlers (multi-select) ──────────────────────────
  // These iterate over the explicit list of session ids the bulk panel passes
  // back. Each operation reuses the single-session machinery so optimistic
  // UI + rollback semantics stay consistent.

  const submitBulkCancelForDate = async (sessionIds: string[], reason: string | null) => {
    // Gate the whole bulk: if any session has attendance recorded on its
    // target date, refuse the entire batch (rather than silently dropping
    // the offending ones — the user should see exactly what to clear).
    const blockChecks = await Promise.all(sessionIds.map(async (sid) => {
      const sess = sessions.find(s => s.id === sid);
      if (!sess || sess.serverId === undefined || sess.childId === null) return [];
      const exDate = getDateForWeekday(sess.dayOfWeek);
      return hasBlockingAttendance(sess.serverId, sess.childId, exDate, exDate);
    }));
    const blockers = blockChecks.flat();
    if (blockers.length > 0) {
      setCancelDateError(buildBlockMessage('cancel', blockers));
      return;
    }
    setCancelDateError(null);
    setCancelDateModalOpen(false);
    setBulkCancelTargetIds([]);
    for (const sid of sessionIds) {
      const sess = sessions.find(s => s.id === sid);
      if (!sess) continue;
      const exDate = getDateForWeekday(sess.dayOfWeek);
      // Skip ones already cancelled for this date.
      if (localExceptions.some(
        ex => ex.sessionId === sid && ex.date === exDate && ex.status === 'cancelled',
      )) continue;
      setLocalExceptions(prev => [
        ...prev,
        { sessionId: sid, date: exDate, status: 'cancelled', reason },
      ]);
      if (sess.serverId !== undefined) {
        ScheduleDataService.createScheduleException({
          scheduleItemServerId: sess.serverId,
          exceptionDate: exDate,
          status: 'cancelled',
          reason: reason ?? undefined,
        }).then(({ id }) => {
          setLocalExceptions(prev =>
            prev.map(ex =>
              ex.sessionId === sid && ex.date === exDate && ex.status === 'cancelled' && ex.serverId === undefined
                ? { ...ex, serverId: id }
                : ex,
            ),
          );
        }).catch(err => {
          setLocalExceptions(prev =>
            prev.filter(ex =>
              !(ex.sessionId === sid && ex.date === exDate && ex.status === 'cancelled' && ex.serverId === undefined),
            ),
          );
          // eslint-disable-next-line no-console
          console.error('Bulk cancel: failed for', sid, err);
        });
      }
    }
  };

  // Bulk-cancel entry point — captures which ids were targeted, then opens
  // the reason modal. The modal's confirm fires submitBulkCancelForDate.
  const [bulkCancelTargetIds, setBulkCancelTargetIds] = useState<string[]>([]);
  const handleBulkCancelForDate = (sessionIds: string[]) => {
    setBulkCancelTargetIds(sessionIds);
    setCancelDateError(null);
    setCancelDateModalOpen(true);
  };

  const handleBulkRestore = (sessionIds: string[]) => {
    for (const sid of sessionIds) {
      const sess = sessions.find(s => s.id === sid);
      if (!sess) continue;
      const exDate = getDateForWeekday(sess.dayOfWeek);
      const target = localExceptions.find(
        ex => ex.sessionId === sid && ex.date === exDate && ex.status === 'cancelled',
      );
      if (!target) continue;
      if (target.serverId !== undefined) {
        ScheduleDataService.deleteScheduleException(target.serverId).catch(err => {
          // eslint-disable-next-line no-console
          console.error('Bulk restore: failed for', sid, err);
        });
      }
    }
    setLocalExceptions(prev =>
      prev.filter(
        ex =>
          !(sessionIds.includes(ex.sessionId) && ex.status === 'cancelled' &&
            sessions.find(s => s.id === ex.sessionId)?.dayOfWeek != null &&
            ex.date === getDateForWeekday(sessions.find(s => s.id === ex.sessionId)!.dayOfWeek)),
      ),
    );
  };

  // Select every visible session in a given day's row. Plain click replaces
  // the current selection; Shift-click adds to it (so the user can stack
  // e.g. Mon + Tue for a long-weekend cancellation).
  const handleSelectDay = (dayName: string, additive: boolean) => {
    const idsForDay = sessions
      .filter(s => s.dayOfWeek === dayName && isSessionVisible(s))
      .map(s => s.id);
    if (idsForDay.length === 0) return;

    if (additive) {
      // Merge into the existing selection. Primary stays as-is; everything
      // else lands in extras (de-duplicated).
      const next = new Set(extraSelectedIds);
      for (const id of idsForDay) {
        if (id !== selectedSessionId) next.add(id);
      }
      // If there's no primary yet, promote the first day-id into primary.
      if (selectedSessionId == null && idsForDay.length > 0) {
        setSelectedSessionId(idsForDay[0]);
        next.delete(idsForDay[0]);
      }
      setExtraSelectedIds(next);
    } else {
      // Replace: first becomes primary, rest become extras.
      const [head, ...rest] = idsForDay;
      setSelectedSessionId(head);
      setExtraSelectedIds(new Set(rest));
    }
    setPanelOpen(true);
  };

  const handleBulkSetTeachers = async (sessionIds: string[], teacherIds: number[]): Promise<void> => {
    // Persist on each rule directly (affects all weeks). Single-session
    // exceptions for week-specific teacher overrides remain available via
    // single-edit + scope modal. The promise resolves once every PATCH has
    // settled, so the bulk panel can show a "Saved ✓" confirmation; if any
    // PATCH rejects we throw so the panel surfaces an error state.
    setSessions(prev =>
      prev.map(s =>
        sessionIds.includes(s.id) ? { ...s, teacherIds: [...teacherIds] } : s,
      ),
    );
    const patches = sessionIds.map(sid => {
      const sess = sessions.find(s => s.id === sid);
      if (!sess || sess.serverId === undefined) return Promise.resolve();
      return ScheduleDataService.updateScheduleItem(sess.serverId, { teacherIds });
    });
    const results = await Promise.allSettled(patches);
    const failures = results.filter(r => r.status === 'rejected');
    if (failures.length > 0) {
      // eslint-disable-next-line no-console
      console.error('Bulk teachers: failed for', failures.length, 'session(s)', failures);
      throw new Error(`${failures.length} session(s) failed to save`);
    }
  };

  /**
   * Write new values onto an exception that already exists, optimistically.
   * Only reachable for a date that is already overridden, so this is always a
   * PATCH — never a create, and never a change to the rule.
   */
  const commitOverrideEdit = (
    target: LocalException,
    next: {
      startTime: string;
      endTime: string;
      // Omitted by a drag-resize, which changes times and nothing else. Leaving
      // them out means the PATCH doesn't mention the bridges at all, so the
      // override keeps whatever teachers and rooms it already had — passing the
      // rule's through instead would silently pin them to this date.
      teacherIds?: number[];
      locationIds?: number[];
    },
    // What a failed save puts back. Defaults to `target`; the guarded caller
    // passes the pre-edit exception, having already applied its own copy.
    restoreTo: LocalException = target,
  ) => {
    const previous = target;
    setLocalExceptions(prev => prev.map(ex =>
      ex === previous
        ? {
          ...ex,
          overrideStartTime: next.startTime,
          overrideEndTime: next.endTime,
          ...(next.teacherIds !== undefined ? { overrideTeacherIds: next.teacherIds } : {}),
          ...(next.locationIds !== undefined ? { overrideLocationIds: next.locationIds } : {}),
        }
        : ex,
    ));
    // No serverId yet means the create that made this override is still in
    // flight, so there's nothing to PATCH. The local change stands and the
    // server keeps the older times until the next refresh — a sub-second race
    // that needs create-promise tracking (as sessions have) to close properly.
    if (target.serverId === undefined) return;
    const serverId = target.serverId;
    ScheduleDataService.updateScheduleException(serverId, {
      overrideStartTime: next.startTime,
      overrideEndTime: next.endTime,
      ...(next.teacherIds !== undefined ? { teacherIds: next.teacherIds } : {}),
      ...(next.locationIds !== undefined ? { locationIds: next.locationIds } : {}),
    }).catch(err => {
      setLocalExceptions(prev => prev.map(ex =>
        ex.serverId === serverId ? restoreTo : ex,
      ));
      // eslint-disable-next-line no-console
      console.error('Failed to update modified exception', err);
      reportExceptionWriteFailure('change this date', err);
    });
  };

  // Every straight-through write to a date's time change goes through here.
  // Those writes skip the scope modal, and with it the attendance guard that
  // lives there — so a date that already had a time change, and attendance
  // recorded on it, could be retimed with no check at all. Resolves false
  // when refused (the caller keeps its draft).
  const commitOverrideEditGuarded = async (
    target: LocalException,
    next: Parameters<typeof commitOverrideEdit>[1],
  ): Promise<boolean> => {
    // Show the new time straight away — otherwise the bar falls back to its
    // old slot while attendance is looked up, then jumps. Put back if refused.
    const optimistic: LocalException = {
      ...target,
      overrideStartTime: next.startTime,
      overrideEndTime: next.endTime,
      ...(next.teacherIds !== undefined ? { overrideTeacherIds: next.teacherIds } : {}),
      ...(next.locationIds !== undefined ? { overrideLocationIds: next.locationIds } : {}),
    };
    setLocalExceptions(prev => prev.map(ex => (ex === target ? optimistic : ex)));

    const session = sessions.find(s => s.id === target.sessionId);
    const serverId = session?.serverId ?? sessionServerIdsRef.current.get(target.sessionId);
    if (serverId !== undefined && session?.childId != null) {
      const blockers = await hasBlockingAttendance(serverId, session.childId, target.date, target.date);
      if (blockers.length > 0) {
        setLocalExceptions(prev => prev.map(ex => (ex === optimistic ? target : ex)));
        await showAttendanceBlock("Can't change this date", blockers);
        return false;
      }
    }
    // Saves (the local change is already in place), and on a failed save
    // restores the date as it was before this edit, not the optimistic copy.
    commitOverrideEdit(optimistic, next, target);
    return true;
  };

  /**
   * Drop this date's override so the occurrence goes back to following the
   * rule. The sibling of handleSessionRestore, which does the same for a
   * 'cancelled' exception.
   */
  const handleRemoveOverride = () => {
    if (!selectedOverride || !baseSelectedSession || !selectedEditDate) return;
    const previous = selectedOverride;
    setLocalExceptions(prev => prev.filter(ex => ex !== previous));
    setDraftEdits(null);
    setDraftSnapshot(null);
    if (previous.serverId === undefined) return;
    ScheduleDataService.deleteScheduleException(previous.serverId).catch(err => {
      // Put it back — the date is still overridden server-side.
      setLocalExceptions(prev => [...prev, previous]);
      // eslint-disable-next-line no-console
      console.error('Failed to delete modified exception', err);
      reportExceptionWriteFailure('revert this date to its usual time', err);
    });
  };

  // Triggered by the panel's Save changes button.
  const handlePanelSave = () => {
    if (!draftEdits || !draftSnapshot) return;
    // Hard block (defensive — Save is also disabled in this state): a cancelled
    // date can't take a one-off edit without un-cancelling itself. Restore first.
    if (isSelectedCancelledThisWeek) return;

    // The date already carries an override and the edit only touches fields the
    // override can hold — so there's no scope question left to ask. Write it
    // straight through; the rule is untouched, as it already was.
    if (savingOverrideOnly && selectedOverride && selectedEditDate && panelSession) {
      if (selectedConflictError !== null) return;  // defensive; Save is disabled too
      // Only mention the bridges if the user actually touched them, so a
      // time-only edit doesn't pin the rule's teachers onto this date.
      const next = {
        startTime: panelSession.startTime,
        endTime: panelSession.endTime,
        ...(draftEdits.teacherIds !== undefined
          ? { teacherIds: panelSession.teacherIds } : {}),
        ...(draftEdits.locationIds !== undefined
          ? { locationIds: panelSession.locationIds } : {}),
      };
      const clearDraft = () => {
        setDraftEdits(null);
        setDraftSnapshot(null);
      };
      // A staff/room-only change disturbs no attendance; a retime does, so it
      // takes the guarded write — and keeps the draft if refused.
      const retimes = next.startTime !== selectedOverride.overrideStartTime
        || next.endTime !== selectedOverride.overrideEndTime;
      if (retimes) {
        void commitOverrideEditGuarded(selectedOverride, next).then(ok => { if (ok) clearDraft(); });
      } else {
        commitOverrideEdit(selectedOverride, next);
        clearDraft();
      }
      return;
    }

    // Hard block (defensive — the Save button is also disabled in this state):
    // refuse to open the scope flow if the pending edit double-books the child.
    const merged = { ...draftSnapshot, ...draftEdits };
    if (findChildConflicts(merged, draftSnapshot.id).length > 0) return;
    // Materialise the draft onto the session so the modal reflects the change.
    setSessions(prev => prev.map(s => (s.id === draftSnapshot.id ? { ...s, ...draftEdits } : s)));
    setScopeError(null);
    // "Just this date" writes a single-date exception, which can only hold
    // times, staff and locations — a date or repeat change would be silently
    // dropped. So there's no scope to ask about: save it as 'persist'.
    const persistOnly = Object.keys(draftEdits).some(
      k => (RULE_ONLY_FIELDS as string[]).includes(k),
    );
    const edit: PendingEdit = {
      sessionId: draftSnapshot.id,
      kind: 'update',
      snapshot: draftSnapshot,
      appliedUpdate: draftEdits,
      editDate: getDateForWeekday(draftSnapshot.dayOfWeek),
      persistOnly,
    };
    setPendingEdit(edit);
    setDraftEdits(null);
    setDraftSnapshot(null);
    if (persistOnly) void handleScopeChoice('persist', edit);
  };

  // Close the panel — confirm if there's a dirty draft. Also "settles" any
  // fresh session so subsequent edits go through the Save+modal flow. If the
  // session was never persisted (still no serverId), it's abandoned and removed.
  const handlePanelClose = async () => {
    if (draftEdits) {
      const ok = await confirmAction({
        title: 'Discard unsaved changes?',
        message: 'Close the panel and discard your unsaved changes to this session?',
        confirmText: 'Discard',
      });
      if (!ok) return;
      setDraftEdits(null);
      setDraftSnapshot(null);
    }
    if (selectedSessionId) {
      const target = sessions.find(s => s.id === selectedSessionId);
      if (target && freshSessionIds.has(selectedSessionId) && target.serverId === undefined) {
        // Abandoned mid-creation (no child picked, never persisted) → drop it.
        removeSessionLocally(selectedSessionId);
      }
      setFreshSessionIds(prev => {
        if (!prev.has(selectedSessionId)) return prev;
        const next = new Set(prev);
        next.delete(selectedSessionId);
        return next;
      });
    }
    setSelectedSessionId(null);
    setPanelOpen(false);
  };

  const handleSessionUpdate = (requested: Partial<ScheduleSession>) => {
    // New-session defaults are edited via SessionDefaultsModal (opened from
    // the "Next new session" pill), not through this panel — the panel only
    // ever edits the selected session.
    if (!selectedSessionId) return;

    const session = sessions.find(s => s.id === selectedSessionId);
    if (!session) return;

    const isFresh = freshSessionIds.has(selectedSessionId) || session.serverId === undefined;
    const current = isFresh ? session : { ...session, ...(draftEdits ?? {}) };
    const updates = alignToStartDate(current, requested);

    if (isFresh) {
      // Live-update the in-memory session.
      setSessions(prev => prev.map(s => (s.id === selectedSessionId ? { ...s, ...updates } : s)));

      const updated = { ...session, ...updates };
      const { id: _id, lane: _lane, serverId: _sid, ...payload } = updated;
      const sessionIdAtCall = selectedSessionId;

      // Prefer the ref over the state-bound serverId — the ref is updated
      // synchronously inside create-success callbacks so it reflects the
      // newly-assigned id even before React has re-rendered with it.
      const effectiveServerId = session.serverId ?? sessionServerIdsRef.current.get(sessionIdAtCall);

      // Hard block: don't persist an edit that double-books the child. The
      // live setSessions above still updates the bar + banner so the user sees
      // the clash and can fix it; persistence resumes once it's resolved.
      if (findChildConflicts(updated, sessionIdAtCall).length > 0) {
        return;
      }

      if (effectiveServerId !== undefined) {
        // Already persisted → live-PATCH.
        const patch = (body: Partial<ScheduleSession>) =>
          ScheduleDataService.updateScheduleItem(effectiveServerId, body).then(() => {
            // Snapshot the latest field values as the next new-session defaults.
            snapshotDefaultsFromSave({
              childId: selectedChildIdNum ?? updated.childId,
              teacherIds: updated.teacherIds,
              locationIds: updated.locationIds,
            });
          }).catch(err => {
            // eslint-disable-next-line no-console
            console.error('Failed to live-PATCH fresh session', err);
          });

        // A fresh session skips the scope modal, and with it the attendance
        // guard — yet a saved one can have attendance against it (an
        // auto-saved draft with a backdated start date). So a change to when
        // it runs is checked here first, by the same in-place rule the modal
        // uses; refused, the bar goes back.
        const retimes = RETIME_FIELDS.some(k => k in updates);
        if (!retimes) {
          // Only what changed: a full payload would also carry times that may
          // still be waiting on their attendance check.
          const { id: _i, lane: _l, serverId: _s, ...changed } = updates;
          void patch(changed);
          return;
        }
        const seq = ++freshRetimeSeqRef.current;
        const childIdAtCall = updated.childId;
        void (childIdAtCall === null
          ? Promise.resolve([] as AttendanceRecord[])
          : attendanceDisturbedInPlace(effectiveServerId, childIdAtCall, session, updated)
        ).then(async blockers => {
          // A later edit supersedes this one — it re-checks and saves the
          // whole session itself, so this one must not land after it.
          if (seq !== freshRetimeSeqRef.current) return;
          if (blockers.length > 0) {
            setSessions(prev => prev.map(s => (s.id === sessionIdAtCall ? session : s)));
            await showAttendanceBlock("Can't change this session", blockers);
            return;
          }
          void patch(payload);
        });
      } else if (createPromisesRef.current.has(sessionIdAtCall)) {
        // A create is already in flight — chain a PATCH after it resolves so
        // we don't fire a duplicate create. Each handleSessionUpdate call
        // queues its own PATCH for its specific updates; the API merges.
        createPromisesRef.current.get(sessionIdAtCall)!
          .then(({ id }) => ScheduleDataService.updateScheduleItem(id, payload))
          .then(() => {
            snapshotDefaultsFromSave({
              childId: selectedChildIdNum ?? updated.childId,
              teacherIds: updated.teacherIds,
              locationIds: updated.locationIds,
            });
          })
          .catch(err => {
            // eslint-disable-next-line no-console
            console.error('Failed to PATCH after pending create', err);
          });
      } else if (updated.childId !== null) {
        // No serverId, no in-flight create, and we now have a child → first-time create.
        const promise = ScheduleDataService.createScheduleItem(payload);
        createPromisesRef.current.set(sessionIdAtCall, promise);
        promise.then(({ id: newServerId }) => {
          sessionServerIdsRef.current.set(sessionIdAtCall, newServerId);
          createPromisesRef.current.delete(sessionIdAtCall);
          setSessions(prev =>
            prev.map(s => (s.id === sessionIdAtCall ? { ...s, serverId: newServerId } : s)),
          );
          snapshotDefaultsFromSave({
            childId: selectedChildIdNum ?? updated.childId,
            teacherIds: updated.teacherIds,
            locationIds: updated.locationIds,
          });
        }).catch(err => {
          createPromisesRef.current.delete(sessionIdAtCall);
          // eslint-disable-next-line no-console
          console.error('Failed to first-time-create fresh session', err);
        });
      }
      return;
    }

    // Settled session: accumulate the change in the draft buffer; sessions[]
    // is not touched until the user clicks Save changes.
    if (!draftSnapshot) setDraftSnapshot(session);
    setDraftEdits(prev => ({ ...(prev ?? {}), ...updates }));
  };

  // Pure local removal + lane compaction. Used by both the no-serverId fast path
  // and the post-modal commit path.
  const removeSessionLocally = (sessionId: string) => {
    sessionServerIdsRef.current.delete(sessionId);
    setSessions(prev => {
      const deleted = prev.find(s => s.id === sessionId);
      const remaining = prev.filter(s => s.id !== sessionId);
      if (!deleted) return remaining;

      // Compact lanes for the affected day: remove gaps
      const day = deleted.dayOfWeek;
      const daySessionsByLane = new Map<number, ScheduleSession[]>();
      for (const s of remaining) {
        if (s.dayOfWeek !== day) continue;
        const list = daySessionsByLane.get(s.lane) || [];
        list.push(s);
        daySessionsByLane.set(s.lane, list);
      }
      const usedLanes = Array.from(daySessionsByLane.keys()).sort((a, b) => a - b);
      const laneMap = new Map<number, number>();
      usedLanes.forEach((oldLane, newLane) => laneMap.set(oldLane, newLane));

      return remaining.map(s => {
        if (s.dayOfWeek !== day) return s;
        const newLane = laneMap.get(s.lane);
        return newLane !== undefined && newLane !== s.lane ? { ...s, lane: newLane } : s;
      });
    });
  };

  // Fresh-mode delete: the session is still in its initial-creation window,
  // so "delete" means "abandon this draft." Hard-remove locally; also tell
  // the server if the auto-save already produced a serverId. No scope modal —
  // settled-mode deletes go through handleCancelForDate / handleDeleteFromDate.
  // Even fresh sessions are gated by the attendance-block guard: an
  // auto-saved draft with a backdated startDate could already have
  // attendance recorded against it.
  const handleSessionDelete = async () => {
    if (!selectedSessionId) return;
    const target = sessions.find(s => s.id === selectedSessionId);
    if (!target) return;

    if (target.serverId !== undefined && target.childId !== null) {
      const blockers = await hasBlockingAttendance(
        target.serverId,
        target.childId,
        target.startDate,
      );
      if (blockers.length > 0) {
        await confirmAction({
          title: "Can't delete this session",
          message: buildBlockMessage('delete', blockers),
          confirmText: 'OK',
          hideCancel: true,
        });
        return;
      }
    }

    if (target.serverId !== undefined) {
      ScheduleDataService.deleteScheduleItem(target.serverId);
    }
    removeSessionLocally(selectedSessionId);
    setSelectedSessionId(null);
    setPanelOpen(false);
  };

  // "Just this date" for a move to another day. A single-date exception can
  // hold other times but not another day, so the move is two records: this
  // date cancelled (reason "Moved to …", so the greyed bar says where it went)
  // and a one-off on the new date. They aren't linked — restoring the
  // cancelled date leaves the one-off in place.
  //
  // Optimistic: both appear at once. The cancellation is written first; if
  // the one-off then fails, the cancellation is undone so the child isn't
  // left with no session that week.
  const moveOneDateToAnotherDay = (
    sessionId: string,
    snapshot: ScheduleSession,
    serverId: number,
    fromDate: string,
    moved: Partial<ScheduleSession>,
  ) => {
    const toDate = moved.startDate!;
    const override = localExceptions.find(
      e => e.sessionId === sessionId && e.date === fromDate && e.status === 'modified',
    );
    const reason = `Moved to ${formatDisplayDate(toDate)}`;
    const oneOff: ScheduleSession = {
      ...snapshot,
      id: `session-${nextSessionId.current++}`,
      serverId: undefined,
      dayOfWeek: moved.dayOfWeek ?? snapshot.dayOfWeek,
      startTime: moved.startTime ?? snapshot.startTime,
      endTime: moved.endTime ?? snapshot.endTime,
      // Staff and rooms as that date actually had them.
      teacherIds: override?.overrideTeacherIds ?? snapshot.teacherIds,
      locationIds: override?.overrideLocationIds ?? snapshot.locationIds,
      frequency: 'once',
      startDate: toDate,
      endDate: toDate,
    };
    const cancelled: LocalException = { sessionId, date: fromDate, status: 'cancelled', reason };

    // The rule itself doesn't change; the moved date becomes the one-off.
    setSessions(prev => [...prev.map(s => (s.id === sessionId ? snapshot : s)), oneOff]);
    setLocalExceptions(prev => [...prev.filter(ex => ex !== override), cancelled]);
    setSelectedSessionId(oneOff.id);

    const rollBackLocal = () => {
      setSessions(prev => prev.filter(s => s.id !== oneOff.id));
      setLocalExceptions(prev => [
        ...prev.filter(ex => !(ex.sessionId === sessionId && ex.date === fromDate)),
        ...(override ? [override] : []),
      ]);
      setSelectedSessionId(sessionId);
    };

    // A date holds one exception whatever its status, so a date that already
    // has a time change is converted to the cancellation rather than added to.
    const writeCancellation: Promise<number> = override?.serverId !== undefined
      ? ScheduleDataService.updateScheduleException(override.serverId, {
        status: 'cancelled', overrideStartTime: null, overrideEndTime: null, reason,
      }).then(() => override.serverId!)
      : ScheduleDataService.createScheduleException({
        scheduleItemServerId: serverId, exceptionDate: fromDate, status: 'cancelled', reason,
      }).then(({ id }) => id);

    writeCancellation.then(cancelId => {
      setLocalExceptions(prev => prev.map(ex => (ex === cancelled ? { ...ex, serverId: cancelId } : ex)));
      const { id: _id, lane: _lane, serverId: _sid, ...payload } = oneOff;
      return ScheduleDataService.createScheduleItem(payload)
        .then(({ id: newServerId }) => {
          sessionServerIdsRef.current.set(oneOff.id, newServerId);
          setSessions(prev => prev.map(s => (s.id === oneOff.id ? { ...s, serverId: newServerId } : s)));
        })
        .catch(err => {
          // Put the date back as it was on the server too.
          const undo = override?.serverId !== undefined
            ? ScheduleDataService.updateScheduleException(override.serverId, {
              status: 'modified',
              overrideStartTime: override.overrideStartTime ?? null,
              overrideEndTime: override.overrideEndTime ?? null,
              reason: null,
            })
            : ScheduleDataService.deleteScheduleException(cancelId);
          return undo.finally(() => { throw err; });
        });
    }).catch(err => {
      rollBackLocal();
      // eslint-disable-next-line no-console
      console.error('Failed to move a single date to another day', err);
      reportExceptionWriteFailure('move this date', err);
    });
  };

  // Modal callback — applies the user's scope choice to the pending edit.
  // `edit` is passed explicitly when the caller has only just queued it (see
  // handlePanelSave), since the pendingEdit state won't have caught up yet.
  const handleScopeChoice = async (scope: EditScope, edit: PendingEdit | null = pendingEdit) => {
    if (!edit) return;
    const { sessionId, kind, snapshot, appliedUpdate, editDate } = edit;

    if (scope === 'cancel') {
      // Revert: restore the snapshot (overwrite any optimistic local change).
      setSessions(prev => prev.map(s => (s.id === sessionId ? snapshot : s)));
      setScopeError(null);
      setPendingEdit(null);
      return;
    }

    const serverId = snapshot.serverId;
    if (serverId === undefined) {
      // Shouldn't happen — modal only opens for persisted sessions — but be safe.
      setScopeError(null);
      setPendingEdit(null);
      return;
    }

    // Where a "This date and future" save splits the rule (rules.planSplit).
    const plan = rules.planSplit({
      kind, snapshot, appliedUpdate, editDate,
      viewedDate: getDateForWeekday(snapshot.dayOfWeek),
    });
    const { fromDate, newEndDate, inPlace: wouldOrphanHistory } = plan;

    // Rule-level clash guard, asked only now that the user has chosen 'persist'.
    // "From now on" pushes these times onto EVERY future occurrence, so unlike
    // the one-off path it has to be judged across dates, not just editDate.
    // Deliberately not run for 'one-off': that scope changes a single date,
    // which the caller already vetted, and judging it rule-wide would block
    // legitimate per-date edits over clashes on dates that aren't changing.
    if (scope === 'persist' && kind !== 'delete' && typeof appliedUpdate !== 'string') {
      // Model the replacement rule as it will actually be created: these times,
      // starting at fromDate (see the 'persist' branch below), and carrying none
      // of the old rule's stored overrides — those stay with the rule being
      // end-dated, so they must not colour the judgement here.
      const candidate = {
        ...snapshot,
        ...appliedUpdate,
        startDate: fromDate,
      };
      const ruleConflicts = findChildConflicts(candidate, snapshot.id, true);
      if (ruleConflicts.length > 0) {
        setScopeError(describeChildConflict(candidate, ruleConflicts));
        return;
      }
    }

    // Attendance-block guard. We refuse the operation if attendance recorded
    // against this rule would be disturbed by it — the modal stays open with
    // an inline error so the user can pick the other scope or close and clear
    // the attendance first.
    //   - one-off: only the date being edited.
    //   - versioning (end-date + replacement): every date from the split on,
    //     since those records would be left pointing at the ended rule.
    //   - in-place update: the records stay on the same rule, so only those
    //     that stop being a session of it are disturbed (a start date moved
    //     earlier on the same weekday touches nothing). A time change alters
    //     every past session it's recorded against, so then all of them count.
    if (snapshot.childId !== null) {
      const operation: 'modify' | 'cancel' | 'delete' =
        kind === 'delete' ? 'delete'
          : scope === 'one-off' ? 'modify'
            : 'modify';
      const blockers = await checkAttendanceWindow(serverId, snapshot.childId, rules.attendanceWindowFor({
        scope, plan, snapshot, appliedUpdate,
        occurrenceDate: editDate ?? getDateForWeekday(snapshot.dayOfWeek),
      }));
      if (blockers.length > 0) {
        setScopeError(buildBlockMessage(operation, blockers));
        return;
      }
    }
    setScopeError(null);

    if (scope === 'one-off') {
      const exceptionDate = editDate ?? getDateForWeekday(snapshot.dayOfWeek);
      if (kind === 'move-day' && typeof appliedUpdate !== 'string') {
        moveOneDateToAnotherDay(sessionId, snapshot, serverId, exceptionDate, appliedUpdate);
        setPendingEdit(null);
        return;
      }
      if (kind === 'delete') {
        // Optimistic local insert; patch in serverId on success.
        setLocalExceptions(prev => [
          ...prev,
          { sessionId, date: exceptionDate, status: 'cancelled' },
        ]);
        ScheduleDataService.createScheduleException({
          scheduleItemServerId: serverId,
          exceptionDate,
          status: 'cancelled',
        }).then(({ id }) => {
          setLocalExceptions(prev =>
            prev.map(ex =>
              ex.sessionId === sessionId && ex.date === exceptionDate && ex.status === 'cancelled' && ex.serverId === undefined
                ? { ...ex, serverId: id }
                : ex,
            ),
          );
        }).catch(err => {
          // Roll back the optimistic insert, or the bar stays greyed out
          // locally against a date the server never cancelled.
          setLocalExceptions(prev => prev.filter(ex =>
            !(ex.sessionId === sessionId &&
              ex.date === exceptionDate &&
              ex.status === 'cancelled' &&
              ex.serverId === undefined),
          ));
          // eslint-disable-next-line no-console
          console.error('Failed to write cancellation exception', err);
          reportExceptionWriteFailure('cancel this date', err);
        });
        setSelectedSessionId(null);
        setPanelOpen(false);
      } else {
        // resize / update / move-day "Just this date" → write a 'modified'
        // exception locally (so the renderer applies it ONLY to this date)
        // AND on the server. Critically, REVERT sessions[] to the pre-edit
        // snapshot so the rule itself is unchanged across other weeks.
        // The date may ALREADY carry a 'modified' exception — re-dragging a bar
        // that's been overridden once before is the common case. Since
        // (schedule_item_id, exception_date) is UNIQUE, a second create would
        // come back 409, so patch the existing row in place instead.
        const existing = localExceptions.find(
          e => e.sessionId === sessionId && e.date === exceptionDate && e.status === 'modified',
        );

        // override_start_time and override_end_time are both-or-neither in the
        // schema, so a partial edit (start changed, end untouched) can't just
        // pass the missing half through as undefined — that lands as NULL and
        // trips the CHECK. Fill each side from what the date effectively shows
        // today: its existing override if there is one, else the rule.
        const baseStart = existing?.overrideStartTime ?? snapshot.startTime;
        const baseEnd = existing?.overrideEndTime ?? snapshot.endTime;
        const overrideStartTime =
          (typeof appliedUpdate !== 'string' ? appliedUpdate.startTime : undefined) ?? baseStart;
        const overrideEndTime =
          (typeof appliedUpdate !== 'string' ? appliedUpdate.endTime : undefined) ?? baseEnd;
        const overrideTeacherIds =
          typeof appliedUpdate !== 'string' ? appliedUpdate.teacherIds : undefined;
        const overrideLocationIds =
          typeof appliedUpdate !== 'string' ? appliedUpdate.locationIds : undefined;

        // Revert: sessions[] should reflect the rule, not the per-date diff.
        setSessions(prev => prev.map(s => (s.id === sessionId ? snapshot : s)));

        const existingServerId = existing?.serverId;

        if (existing !== undefined && existingServerId !== undefined) {
          const previous = existing;
          // Optimistically re-point the mirror entry at the new override.
          setLocalExceptions(prev => prev.map(ex =>
            ex === previous
              ? { ...ex, overrideStartTime, overrideEndTime, overrideTeacherIds, overrideLocationIds }
              : ex,
          ));
          ScheduleDataService.updateScheduleException(existingServerId, {
            overrideStartTime,
            overrideEndTime,
            teacherIds: overrideTeacherIds,
            locationIds: overrideLocationIds,
          }).catch(err => {
            // Restore the override the row actually still holds.
            setLocalExceptions(prev => prev.map(ex =>
              ex.serverId === existingServerId ? previous : ex,
            ));
            // eslint-disable-next-line no-console
            console.error('Failed to update modified exception', err);
            reportExceptionWriteFailure('change this date', err);
          });
        } else {
          // Optimistically add the local override; serverId patched in on success.
          setLocalExceptions(prev => [
            ...prev,
            {
              sessionId,
              date: exceptionDate,
              status: 'modified',
              overrideStartTime,
              overrideEndTime,
              overrideTeacherIds,
              overrideLocationIds,
            },
          ]);

          ScheduleDataService.createScheduleException({
            scheduleItemServerId: serverId,
            exceptionDate,
            status: 'modified',
            overrideStartTime,
            overrideEndTime,
            teacherIds: overrideTeacherIds,
            locationIds: overrideLocationIds,
          }).then(({ id }) => {
            setLocalExceptions(prev => prev.map(ex =>
              ex.sessionId === sessionId &&
              ex.date === exceptionDate &&
              ex.status === 'modified' &&
              ex.serverId === undefined
                ? { ...ex, serverId: id }
                : ex,
            ));
          }).catch(err => {
            // Roll back the optimistic insert on failure.
            setLocalExceptions(prev => prev.filter(ex =>
              !(ex.sessionId === sessionId &&
                ex.date === exceptionDate &&
                ex.status === 'modified' &&
                ex.serverId === undefined),
            ));
            // eslint-disable-next-line no-console
            console.error('Failed to write modified exception', err);
            reportExceptionWriteFailure('change this date', err);
          });
        }

        // Defaults snapshot: prefer the override values the user just made.
        snapshotDefaultsFromSave({
          childId: selectedChildIdNum ?? snapshot.childId,
          teacherIds: overrideTeacherIds ?? snapshot.teacherIds,
          locationIds: overrideLocationIds ?? snapshot.locationIds,
        });
      }
    } else {
      // 'persist' → end-date the old rule at newEndDate, then create a
      // replacement starting from fromDate — or, with no history before the
      // split (wouldOrphanHistory), mutate the rule in place (PATCH or DELETE).
      // Sequenced so that if the end-date fails, we don't end up with an
      // orphaned replacement rule alongside the old one.

      if (kind === 'delete') {
        if (wouldOrphanHistory) {
          // Rule has no past instances — hard-delete instead of end-dating.
          ScheduleDataService.deleteScheduleItem(serverId)
            .then(() => {
              removeSessionLocally(sessionId);
              setSelectedSessionId(null);
              setPanelOpen(false);
            })
            .catch(err => {
              // eslint-disable-next-line no-console
              console.error('Failed to hard-delete schedule_item', err);
            });
        } else {
          ScheduleDataService.endDateScheduleItem(serverId, newEndDate)
            .then(() => {
              setSessions(prev =>
                prev.map(s => (s.id === sessionId ? { ...s, endDate: newEndDate } : s)),
              );
              setSelectedSessionId(null);
              setPanelOpen(false);
            })
            .catch(err => {
              // eslint-disable-next-line no-console
              console.error('Failed to end-date schedule_item', err);
            });
        }
      } else {
        // Built from the edit itself, not read back from `sessions`: when
        // handlePanelSave saves without asking, this runs in the same tick as
        // its setSessions, so `sessions` still holds the pre-edit rule and the
        // PATCH would quietly re-send the old values.
        const liveSession: ScheduleSession = typeof appliedUpdate === 'string'
          ? snapshot
          : { ...snapshot, ...appliedUpdate };
        const { id: _localId, lane: _lane, serverId: _sid, ...rulePayload } = liveSession;

        // Never shorten the old rule for a replacement the DB will refuse: a
        // rule can't start after it ends. Checked before anything is written.
        // (A drag carries the end date with the move, so this is a backstop.)
        const newStart = wouldOrphanHistory ? liveSession.startDate : fromDate;
        if (liveSession.endDate && newStart > liveSession.endDate) {
          setScopeError(
            `This would start the session on ${formatDisplayDate(newStart)}, after it ends on ` +
            `${formatDisplayDate(liveSession.endDate)}. Change its end date first.`,
          );
          return;
        }

        if (wouldOrphanHistory) {
          // No history to preserve → just update the existing rule in place.
          ScheduleDataService.updateScheduleItem(serverId, rulePayload)
            .then(() => {
              snapshotDefaultsFromSave({
                childId: selectedChildIdNum ?? liveSession.childId,
                teacherIds: liveSession.teacherIds,
                locationIds: liveSession.locationIds,
              });
            })
            .catch(err => {
              // eslint-disable-next-line no-console
              console.error('Failed to in-place-update schedule_item', err);
            });
        } else {
          // Chain: end-date first; only create the replacement on success.
          ScheduleDataService.endDateScheduleItem(serverId, newEndDate)
            .then(() => ScheduleDataService.createScheduleItem({ ...rulePayload, startDate: fromDate }))
            .then(({ id: newServerId }) => {
              // Mirror the split locally: this session (and the open panel)
              // becomes the replacement from fromDate, and the old rule lives
              // on as its own end-dated session so the weeks before fromDate
              // keep their bar without a reload. Exceptions dated within the
              // old rule's remaining run belong to it, so they move with it.
              const pastId = `session-${nextSessionId.current++}`;
              sessionServerIdsRef.current.set(sessionId, newServerId);
              sessionServerIdsRef.current.set(pastId, serverId);
              setSessions(prev => prev.flatMap(s => (s.id === sessionId
                ? [
                  { ...snapshot, id: pastId, endDate: newEndDate },
                  { ...liveSession, id: sessionId, lane: s.lane, startDate: fromDate, serverId: newServerId },
                ]
                : [s])));
              setLocalExceptions(prev => prev.map(ex => (
                ex.sessionId === sessionId && ex.date <= newEndDate ? { ...ex, sessionId: pastId } : ex
              )));
              snapshotDefaultsFromSave({
                childId: selectedChildIdNum ?? liveSession.childId,
                teacherIds: liveSession.teacherIds,
                locationIds: liveSession.locationIds,
              });
            })
            .catch(err => {
              // eslint-disable-next-line no-console
              console.error('Failed to version schedule_item (end-date or create step)', err);
            });
        }
      }
    }

    setPendingEdit(null);
  };

  // ── Hovered column index for time bars ─────────────────────

  const hoveredIdx = (() => {
    if (!hoveredCell) return -1;
    const last = hoveredCell.lastIndexOf(':');
    return Number(hoveredCell.substring(last + 1));
  })();

  // ── Hovered session ID ────────────────────────────────────

  const hoveredSessionId = (() => {
    if (!hoveredCell) return null;
    const last = hoveredCell.lastIndexOf(':');
    const secondLast = hoveredCell.lastIndexOf(':', last - 1);
    const day = hoveredCell.substring(0, secondLast);
    const lane = Number(hoveredCell.substring(secondLast + 1, last));
    const idx = Number(hoveredCell.substring(last + 1));
    const s = getSessionAt(day, lane, idx);
    return s?.id ?? null;
  })();

  // A move released outside the schedule never reaches handleMouseUp. Left
  // alone, the preview would stick and the next click inside would drop it,
  // saving a move nobody made — so a release anywhere else abandons it.
  // (Window listeners run after React's, so a release inside has already
  // been handled and reset dragTypeRef by the time this looks.)
  // Same for a press that never left its cell: left primed, the next time
  // the pointer crossed a cell — button up — it would start dragging.
  // Also what the schedule's onMouseLeave calls: dragging off it abandons.
  const abandonMove = () => {
    dragTypeRef.current = 'none';
    movePreviewRef.current = null;
    setMovePreview(null);
    setPanelOpen(true);  // the bar is still selected; show it as a click would
  };
  useEffect(() => {
    const onRelease = () => {
      if (dragTypeRef.current === 'move' || dragTypeRef.current === 'move-pending') abandonMove();
    };
    window.addEventListener('mouseup', onRelease);
    return () => window.removeEventListener('mouseup', onRelease);
    // abandonMove only calls state setters and a ref — stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A bar mid-drag that would double-book its child where it currently sits.
  // Drawn grey — out of the child's colour, so it reads as "not here" — so
  // the refusal on drop isn't a surprise.
  const moveClashes = (() => {
    if (!movePreview) return false;
    const s = sessions.find(x => x.id === movePreview.sessionId);
    return !!s && findChildConflictsOnDate(
      s, s.id, getDateForWeekday(movePreview.day), movePreview,
    ).length > 0;
  })();

  // ── Draft bounds helpers ───────────────────────────────────

  const isDraftSlot = (day: string, lane: number, idx: number): boolean => {
    if (!draftBounds || draftBounds.day !== day || draftBounds.lane !== lane) return false;
    const lo = Math.min(draftBounds.startIdx, draftBounds.endIdx);
    const hi = Math.max(draftBounds.startIdx, draftBounds.endIdx);
    return idx >= lo && idx <= hi;
  };

  const isDraftStart = (day: string, lane: number, idx: number): boolean => {
    if (!draftBounds || draftBounds.day !== day || draftBounds.lane !== lane) return false;
    return idx === Math.min(draftBounds.startIdx, draftBounds.endIdx);
  };

  const isDraftEnd = (day: string, lane: number, idx: number): boolean => {
    if (!draftBounds || draftBounds.day !== day || draftBounds.lane !== lane) return false;
    return idx === Math.max(draftBounds.startIdx, draftBounds.endIdx);
  };

  // ── Per-day date helpers ───────────────────────────────────

  const dayMeta = (() => {
    if (!weekStart) return {} as Record<string, { display: string }>;
    const base = new Date(weekStart + 'T00:00');
    const names = [...WEEKDAYS, ...WEEKEND];
    const offsets: Record<string, number> = {
      Monday: 0, Tuesday: 1, Wednesday: 2, Thursday: 3,
      Friday: 4, Saturday: 5, Sunday: 6,
    };
    const out: Record<string, { display: string }> = {};
    for (const n of names) {
      const d = new Date(base);
      d.setDate(d.getDate() + offsets[n]);
      // dd MMM (year omitted in the grid — it's already implied by the
      // surrounding week navigator label).
      out[n] = { display: formatDisplayDate(d).replace(/\s\d{4}$/, '') };
    }
    return out;
  })();

  // ── Time bar ───────────────────────────────────────────────

  const timeBar = (position: 'top' | 'bottom') => (
    <div className={`wsg-timebar wsg-timebar--${position}`}>
      <div className="wsg-timebar__gutter" />
      <div className="wsg-timebar__track">
        {slots.map((slot, idx) => {
          const isColHovered = idx === hoveredIdx;
          return (
            <div
              key={idx}
              className={`wsg-timebar__cell${isColHovered ? ' is-hovered' : ''}`}
            >
              {isColHovered ? (
                <span className="wsg-timebar__range">
                  {slot.start}&ndash;{slot.end}
                </span>
              ) : slot.hourStart ? (
                <span className="wsg-timebar__hour">{slot.start}</span>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );

  // ── Render ─────────────────────────────────────────────────

  return (
    <div
      style={{ display: 'flex', width: '100%', minHeight: 0 }}
      onMouseUp={handleMouseUp}
      onMouseLeave={() => {
        // Dragging a bar off the schedule is how a move is called off, so it
        // is abandoned rather than dropped at its last preview. (Create and
        // resize still finish on leave, as they always have.)
        if (dragTypeRef.current === 'move' || dragTypeRef.current === 'move-pending') {
          abandonMove();
        } else {
          handleMouseUp();
        }
        setHoveredCell(null);
      }}
    >
      {/* Main grid column — clicking empty area deselects */}
      <div
        style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
        onMouseDown={() => {
          setSelectedSessionId(null);
          setExtraSelectedIds(new Set());
        }}
      >
        {weekStart && (
          <WeekNavigator weekStart={weekStart} onWeekChange={onWeekChange} allowFuture />
        )}
        <div className="wsg-root" style={{ flex: 1, overflow: 'auto' }}>
          <div className="wsg-card">
            {/* Header row */}
            <div className="wsg-header">
              <div>
                <h4 className="wsg-heading">
                  Weekly Schedule
                  {childName && (
                    <>
                      <span className="wsg-heading__dash">&mdash;</span>
                      <span className="wsg-heading__child">{childName}</span>
                    </>
                  )}
                </h4>
                {/* Defaults pill — opens SessionDefaultsModal. The pill always
                    labels its own action; the getting-started hint sits beside
                    it as plain text so the label never does double duty. */}
                {(() => {
                  const defaultChild = defaultChildProfile;
                  const defaultTeacherNames = panelDefaults.teacherIds
                    .map(tid => editorStaff.find(s => s.id === tid)?.name)
                    .filter((n): n is string => !!n);
                  const teachersLabel = defaultTeacherNames.length === 0
                    ? null
                    : defaultTeacherNames.length <= 2
                      ? defaultTeacherNames.join(', ')
                      : `${defaultTeacherNames.length} teachers`;
                  const locationCount = panelDefaults.locationIds.length;
                  const locationLabel = locationCount === 0
                    ? null
                    : locationCount === 1
                      ? '1 location'
                      : `${locationCount} locations`;
                  return (
                    <div className="wsg-defaults-row">
                      <button
                        type="button"
                        className="wsg-defaults-pill"
                        data-help="schedule.defaults-pill"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => setDefaultsModalOpen(true)}
                        title="Edit defaults for new sessions"
                      >
                        <i className="fas fa-sliders wsg-defaults-pill__icon" aria-hidden="true" />
                        {defaultChild ? (
                          <>
                            <span className="wsg-defaults-pill__lead">Session defaults:</span>
                            <span
                              className="wsg-defaults-pill__dot"
                              style={{ background: defaultChild.colour_code || '#aaa' }}
                            />
                            <strong>{defaultChild.first_name} {defaultChild.last_name}</strong>
                            {teachersLabel && <span className="wsg-defaults-pill__sep">·</span>}
                            {teachersLabel && teachersPinned && (
                              <i
                                className="fas fa-thumbtack wsg-defaults-pill__pin"
                                title="Staff default pinned via the defaults popup"
                                aria-label="Staff default pinned"
                              />
                            )}
                            {teachersLabel && <span>{teachersLabel}</span>}
                            {locationLabel && <span className="wsg-defaults-pill__sep">·</span>}
                            {locationLabel && <span>{locationLabel}</span>}
                            <span className="wsg-defaults-pill__sep">·</span>
                            <span title="Length of sessions added with a single click">
                              {defaultSessionLengthMinutes >= 60
                                ? `${defaultSessionLengthMinutes / 60} hr`
                                : `${defaultSessionLengthMinutes} min`}
                            </span>
                          </>
                        ) : (
                          <span>Session defaults</span>
                        )}
                      </button>

                      {/* Getting-started hint — plain text, not a control. Only
                          while there's no default child to inherit from. */}
                      {!defaultChild && (
                        <span className="wsg-defaults-hint">
                          Pick a child, or drag on the grid to add a session.
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div className="wsg-config">
                <button
                  type="button"
                  className="wsg-bulkbtn"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={() => setBulkPeriodOpen(true)}
                  title="Cancel or restore sessions across a date range"
                >
                  <i className="fas fa-calendar-xmark" aria-hidden="true" />
                  Bulk update…
                </button>
                {/* Wrapped for the same reason as the week navigator:
                    GridLockToggle is shared with Weekly Attendance. */}
                <span data-help="schedule.grid-lock" style={{ display: 'inline-flex' }}>
                  <GridLockToggle
                    locked={gridLocked}
                    onToggle={() => setGridLocked(v => !v)}
                    flashKey={lockFlashKey}
                  />
                </span>
                {/* Per-page Weekends / Start / End / Session controls
                    intentionally hidden — these settings are now owned at
                    org level via Admin → Organisation → Schedule &
                    Attendance. The state hooks above stay (they're seeded
                    from orgConfig and feed `days` / `slots` downstream)
                    so re-showing the controls later is just a matter of
                    flipping this guard. */}
                {false && (
                  <>
                    <label className="wsg-pill">
                      <input
                        type="checkbox"
                        checked={includeWeekends}
                        onChange={(e) => setIncludeWeekends(e.target.checked)}
                      />
                      Weekends
                    </label>
                    <label className="wsg-pill">
                      <span className="wsg-pill__label">Start</span>
                      <input type="time"
                        value={dayStartTime} onChange={(e) => setDayStartTime(e.target.value)} />
                    </label>
                    <label className="wsg-pill">
                      <span className="wsg-pill__label">End</span>
                      <input type="time"
                        value={dayEndTime} onChange={(e) => setDayEndTime(e.target.value)} />
                    </label>
                    <label className="wsg-pill">
                      <span className="wsg-pill__label">Session</span>
                      <select
                        value={sessionMinutes} onChange={(e) => setSessionMinutes(Number(e.target.value))}>
                        {SESSION_OPTIONS.map(m => (
                          <option key={m} value={m}>{m} min</option>
                        ))}
                      </select>
                    </label>
                  </>
                )}
              </div>
            </div>

            {/* Grid area */}
            <div
              className={`wsg-grid${gridLocked ? '' : ' is-unlocked'}${movePreview ? ' is-moving' : ''}`}
              data-help="schedule.grid"
              style={{ ['--wsg-slot-count' as string]: slots.length } as React.CSSProperties}
              // Locked, a double-click anywhere on the grid asks to unlock —
              // on a bar too, where a single click only opens its details.
              // (Empty cells already get there through lockedClickGuard; a
              // second open is harmless.)
              onDoubleClick={() => {
                if (gridLocked) setUnlockPromptOpen(true);
              }}
              onMouseLeave={() => {
                setHoveredCell(null);
                // Off the grid mid-move: the bar goes back where it was, which
                // is what a release out here does (handleMouseUp). The drag
                // itself stays live — back onto a cell and it picks up again.
                if (dragTypeRef.current === 'move') {
                  movePreviewRef.current = null;
                  setMovePreview(null);
                }
              }}
            >
              {timeBar('top')}

              {days.map((day) => {
                const laneCount = getLaneCount(day);
                const meta = dayMeta[day];
                const isToday = getDateForWeekday(day) === todayDateStr;
                return (
                  <div
                    key={day}
                    className={`wsg-day-row ${isToday ? 'is-today' : ''}`}
                  >
                    <div
                      className="wsg-day-label is-clickable"
                      style={{ minHeight: MIN_LANES * SESSION_HEIGHT }}
                      role="button"
                      tabIndex={0}
                      title={`Select all sessions on ${day} (Shift-click to add to existing selection)`}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleSelectDay(day, e.shiftKey);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSelectDay(day, e.shiftKey);
                        }
                      }}
                    >
                      <span className="wsg-day-name">{day}</span>
                      {meta && (
                        <span className="wsg-day-date">{meta.display}</span>
                      )}
                      {isToday && <span className="wsg-day-today">Today</span>}
                    </div>

                    <div className="wsg-lanes">
                      {isToday && nowPercentInDay !== null && (
                        <div
                          className="wsg-now-line"
                          style={{ left: `${nowPercentInDay}%` }}
                          aria-hidden
                          title={`Now · ${String(nowDate.getHours()).padStart(2, '0')}:${String(nowDate.getMinutes()).padStart(2, '0')}`}
                        />
                      )}
                      {Array.from({ length: laneCount }, (_, lane) => (
                        <div key={lane} className="wsg-lane" style={{ height: SESSION_HEIGHT }}>
                          {slots.map((slot, idx) => {
                            const session = getSessionAt(day, lane, idx);
                            const draft = isDraftSlot(day, lane, idx);
                            const draftStart = isDraftStart(day, lane, idx);
                            const draftEnd = isDraftEnd(day, lane, idx);
                            const isColHovered = idx === hoveredIdx;
                            const isSelected = session ? isSessionSelected(session.id) : false;

                            // Session rendering
                            if (session) {
                              const isMoving = movePreview?.sessionId === session.id;
                              const colour = isMoving && moveClashes ? '#6b7280' :getSessionColour(session);
                              const initials = getSessionInitials(session);
                              const first = isFirstSlot(session, idx);
                              const last = isLastSlot(session, idx);
                              const prevHasSession = idx > 0 && getSessionAt(day, lane, idx - 1)?.id === session.id;
                              const isHovered = hoveredSessionId === session.id;
                              const cancelled = isSessionCancelledThisWeek(session);
                              // This date has its own time ("Just this date") — the bar sits
                              // where it really runs, so mark it as not the usual time. Not
                              // while it's being dragged: the preview isn't that change.
                              const changedForDate = !cancelled && !isMoving && localExceptions.some(
                                e => e.sessionId === session.id
                                  && e.date === getDateForWeekday(session.dayOfWeek)
                                  && e.status === 'modified',
                              );
                              // Resize handles only on the primary selection
                              // (single-select). Hidden in multi-select to
                              // avoid implying you can resize many at once.
                              const isPrimary = session?.id === selectedSessionId && !isMultiSelect;
                              const showLeftHandle = first && isPrimary && !cancelled && !gridLocked;
                              const showRightHandle = last && isPrimary && !cancelled && !gridLocked;

                              const radiusLeft = first ? '10px' : '0';
                              const radiusRight = last ? '10px' : '0';

                              // Per-side borders so multi-slot sessions have no internal verticals
                              const borderPx = isSelected ? 1.5 : 1;
                              const borderColour = isSelected
                                ? colour
                                : isHovered ? `${colour}a0` : `${colour}55`;
                              const shadowParts: string[] = [
                                `inset 0 ${borderPx}px 0 ${borderColour}`,
                                `inset 0 -${borderPx}px 0 ${borderColour}`,
                              ];
                              if (first && !prevHasSession) {
                                shadowParts.push(`inset 3px 0 0 ${colour}`);
                              }
                              if (last) {
                                shadowParts.push(`inset -${borderPx}px 0 0 ${borderColour}`);
                              }
                              if (isSelected) {
                                shadowParts.push(`0 3px 8px -2px ${colour}99`);
                              } else if (isHovered) {
                                shadowParts.push(`0 2px 6px -2px ${colour}77`);
                              }

                              return (
                                <div
                                  key={idx}
                                  className={`wsg-slot wsg-slot--session${isSelected ? ' is-selected' : ''}${cancelled ? ' is-cancelled' : ''}${isMoving ? ' is-moving' : ''}`}
                                  // Help highlights the bar the user has
                                  // actually picked, not every bar on screen.
                                  data-help={isSelected ? 'schedule.session-selected' : undefined}
                                  onMouseDown={(e) => handleCellMouseDown(e, day, lane, idx)}
                                  onMouseEnter={() => handleCellMouseEnter(day, lane, idx)}
                                  style={{
                                    height: SESSION_HEIGHT,
                                    display: 'flex',
                                    alignItems: 'center',
                                    overflow: 'visible',
                                    borderRadius: `${radiusLeft} ${radiusRight} ${radiusRight} ${radiusLeft}`,
                                    background: cancelled
                                      ? `repeating-linear-gradient(135deg, ${colour}1f 0 6px, ${colour}14 6px 12px)`
                                      : isSelected
                                        ? `linear-gradient(180deg, ${colour}82 0%, ${colour}66 100%)`
                                        : isHovered
                                          ? `linear-gradient(180deg, ${colour}66 0%, ${colour}4d 100%)`
                                          : `linear-gradient(180deg, ${colour}52 0%, ${colour}38 100%)`,
                                    boxShadow: shadowParts.join(', '),
                                    transition: 'background 160ms ease, box-shadow 160ms ease',
                                    opacity: cancelled ? 0.6 : 1,
                                  }}
                                >
                                  {first && initials && (
                                    <span
                                      className="wsg-session-label"
                                      title={changedForDate
                                        ? `Changed for this date only — usually ${session.startTime}–${session.endTime}`
                                        : undefined}
                                      style={{
                                        color: colour,
                                        textDecoration: cancelled ? 'line-through' : 'none',
                                      }}
                                    >
                                      {initials}
                                      {changedForDate && (
                                        <i
                                          className="fas fa-pen wsg-session-label__changed"
                                          role="img"
                                          aria-label="changed for this date only"
                                        />
                                      )}
                                    </span>
                                  )}

                                  {showLeftHandle && (
                                    <div
                                      className="wsg-resize-handle wsg-resize-handle--left"
                                      onMouseDown={(e) => handleResizeMouseDown(e, session.id, 'left')}
                                    >
                                      <div className="wsg-resize-handle__bar" style={{ background: colour }} />
                                    </div>
                                  )}

                                  {showRightHandle && (
                                    <div
                                      className="wsg-resize-handle wsg-resize-handle--right"
                                      onMouseDown={(e) => handleResizeMouseDown(e, session.id, 'right')}
                                    >
                                      <div className="wsg-resize-handle__bar" style={{ background: colour }} />
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            // Draft / empty cell
                            const draftColour = panelDefaults.childId
                              ? (childProfiles.find(c => c.id === panelDefaults.childId)?.colour_code || '#aaaaaa')
                              : '#aaaaaa';

                            // Draft range styling — round only the outer corners so
                            // adjacent draft cells visually join into a single bar.
                            const draftRadLeft = draftStart ? '10px' : '0';
                            const draftRadRight = draftEnd ? '10px' : '0';
                            const draftShadow = draft
                              ? [
                                  `inset 0 1px 0 ${draftColour}55`,
                                  `inset 0 -1px 0 ${draftColour}55`,
                                  draftStart ? `inset 3px 0 0 ${draftColour}` : '',
                                  draftEnd ? `inset -1px 0 0 ${draftColour}a0` : '',
                                ].filter(Boolean).join(', ')
                              : 'none';

                            return (
                              <div
                                key={idx}
                                className={`wsg-slot wsg-slot--empty${isColHovered ? ' wsg-slot--col-hover' : ''}`}
                                onMouseDown={(e) => handleCellMouseDown(e, day, lane, idx)}
                                onMouseEnter={() => handleCellMouseEnter(day, lane, idx)}
                                style={{
                                  height: SESSION_HEIGHT,
                                  borderRadius: draft
                                    ? `${draftRadLeft} ${draftRadRight} ${draftRadRight} ${draftRadLeft}`
                                    : 0,
                                  boxShadow: draftShadow,
                                  background: draft
                                    ? `linear-gradient(180deg, ${draftColour}40 0%, ${draftColour}25 100%)`
                                    : undefined,
                                }}
                              />
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {timeBar('bottom')}
            </div>
          </div>
        </div>
      </div>

      {/* Detail panel — slide in/out */}
      <div data-testid="session-panel-slot" style={{
        width: panelShown ? DETAIL_PANEL_WIDTH : 0,
        minWidth: panelShown ? DETAIL_PANEL_WIDTH : 0,
        borderLeft: panelShown ? '1px solid rgba(38, 146, 63, 0.22)' : 'none',
        overflow: 'hidden',
        transition: 'width 0.2s ease, min-width 0.2s ease',
      }}>
        {panelOpen && isMultiSelect && (() => {
          const selectedSessions = allSelectedIds
            .map(id => sessions.find(s => s.id === id))
            .filter((s): s is ScheduleSession => !!s);
          const datesBySessionId: Record<string, string> = {};
          for (const s of selectedSessions) {
            datesBySessionId[s.id] = getDateForWeekday(s.dayOfWeek);
          }
          const cancelledSet = new Set<string>();
          for (const s of selectedSessions) {
            const d = datesBySessionId[s.id];
            if (localExceptions.some(
              ex => ex.sessionId === s.id && ex.date === d && ex.status === 'cancelled',
            )) cancelledSet.add(s.id);
          }
          return (
            <BulkEditPanel
              sessions={selectedSessions}
              datesBySessionId={datesBySessionId}
              childProfiles={childProfiles}
              staffProfiles={editorStaff}
              cancelledSessionIds={cancelledSet}
              onBulkSetTeachers={handleBulkSetTeachers}
              onBulkCancelForDate={handleBulkCancelForDate}
              onBulkRestore={handleBulkRestore}
              onClose={() => {
                setSelectedSessionId(null);
                setExtraSelectedIds(new Set());
                setPanelOpen(false);
              }}
            />
          );
        })()}
        {panelOpen && !isMultiSelect && panelSession && (
          <SessionDetailPanel
            session={panelSession}
            childProfiles={childProfiles}
            staffProfiles={editorStaff}
            onUpdate={handleSessionUpdate}
            onDelete={handleSessionDelete}
            onClose={handlePanelClose}
            isFresh={isSelectedFresh}
            isDirty={isPanelDirty}
            canSave={canSavePanel}
            onSave={handlePanelSave}
            conflictError={selectedConflictError}
            conflictWarning={selectedConflictWarning}
            cancelledThisDate={isSelectedCancelledThisWeek}
            cancelledReason={selectedCancelledReason}
            editDate={selectedEditDate}
            onRestore={handleSessionRestore}
            overriddenThisDate={selectedOverride !== null}
            ruleTimes={baseSelectedSession
              ? { startTime: baseSelectedSession.startTime, endTime: baseSelectedSession.endTime }
              : null}
            onRemoveOverride={handleRemoveOverride}
            saveLabel={savingOverrideOnly ? 'Save for this date' : 'Save changes'}
            onCancelForDate={handleCancelForDate}
            onDeleteFromDate={handleDeleteFromDate}
          />
        )}
      </div>

      <SessionDefaultsModal
        open={defaultsModalOpen}
        childProfiles={childProfiles}
        initialChildId={panelDefaults.childId}
        childFromFilter={selectedChildIdNum !== null}
        staffProfiles={editorStaff}
        initialTeacherIds={panelDefaults.teacherIds}
        initialSessionLengthMinutes={defaultSessionLengthMinutes}
        teachersPinned={teachersPinned}
        onSave={({ childId: nextChildId, teacherIds, sessionLengthMinutes }) => {
          setPanelDefaults(prev => ({ ...prev, childId: nextChildId, teacherIds }));
          setDefaultSessionLengthMinutes(sessionLengthMinutes);
          setTeachersPinnedAll(true);
          setDefaultsModalOpen(false);
        }}
        onResetAuto={() => {
          // Unpin only — current values stay until the next session save
          // re-derives them.
          setTeachersPinnedAll(false);
        }}
        onClose={() => setDefaultsModalOpen(false)}
      />

      <ScopeChoiceModal
        open={pendingEdit !== null && (!pendingEdit.persistOnly || scopeError !== null)}
        persistOnly={pendingEdit?.persistOnly ?? false}
        kind={pendingEdit?.kind ?? 'update'}
        editDate={pendingEdit?.editDate ?? null}
        errorMessage={scopeError}
        errorHelp={{ guideKey: 'attendance.remove-record', label: 'How do I remove an attendance record?' }}
        onChoice={handleScopeChoice}
      />

      <UnlockPromptModal
        open={unlockPromptOpen}
        intent="create-session"
        onUnlock={() => {
          setGridLocked(false);
          setUnlockPromptOpen(false);
        }}
        onCancel={() => setUnlockPromptOpen(false)}
      />

      <CancelDateModal
        open={cancelDateModalOpen}
        editDate={
          bulkCancelTargetIds.length > 0 ? null : selectedEditDate
        }
        count={bulkCancelTargetIds.length > 0 ? bulkCancelTargetIds.length : 1}
        errorMessage={cancelDateError}
        errorHelp={{ guideKey: 'attendance.remove-record', label: 'How do I remove an attendance record?' }}
        onConfirm={(reason) => {
          if (bulkCancelTargetIds.length > 0) {
            // submitBulkCancelForDate clears the bulk target list itself on
            // success; on a block it keeps both modal and targets so the
            // user can fix attendance and retry.
            submitBulkCancelForDate(bulkCancelTargetIds, reason);
          } else {
            submitCancelForDate(reason);
          }
        }}
        onCancel={() => {
          setCancelDateModalOpen(false);
          setCancelDateError(null);
          setBulkCancelTargetIds([]);
        }}
      />

      <BulkPeriodModal
        open={bulkPeriodOpen}
        sessions={sessions.filter(s => s.childId != null && allowedChildIds.has(s.childId))}
        childProfiles={childProfiles}
        exceptions={localExceptions}
        weekStart={weekStart ?? toDateString(getMonday(new Date()))}
        days={days}
        initialChildId={selectedChildIdNum}
        onApplied={refreshExceptions}
        onClose={() => setBulkPeriodOpen(false)}
      />

      <ConfirmationModal
        show={confirmConfig !== null}
        title={confirmConfig?.title ?? ''}
        message={confirmConfig?.message ?? ''}
        confirmText={confirmConfig?.confirmText}
        cancelText={confirmConfig?.cancelText}
        isDeleting={confirmConfig?.isDeleting}
        hideCancel={confirmConfig?.hideCancel}
        help={confirmConfig?.help}
        onConfirm={() => {
          confirmResolveRef.current?.(true);
          confirmResolveRef.current = null;
          setConfirmConfig(null);
        }}
        onCancel={() => {
          confirmResolveRef.current?.(false);
          confirmResolveRef.current = null;
          setConfirmConfig(null);
        }}
      />
    </div>
  );
};

export default WeeklySchedule;
